import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Check,
  CheckCircle2,
  CheckCheck,
  Edit3,
  Loader2,
  MessageSquarePlus,
  RefreshCw,
  Sparkles,
  X,
  ArrowRight,
} from 'lucide-react'

import { api, ApiError, projectFileUrl } from '@/lib/api'
import {
  coerceToAcceptOriginal,
  submitWithRecovery,
} from '@/lib/g4SubmitRecovery'
import { waitForPhaseCompletion } from '@/lib/phaseEvents'
import { decisionLabel } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import type { GateDecisionRecord, GateDecisionStatus, Project } from '@/types'
import {
  gateDraftKey,
  reconcileDraftWithGate,
  useGateDraft,
  useGateDraftStore,
} from '@/stores/gateDraftStore'
import {
  BULK_ACCEPT_RATIONALE,
  BULK_BATCH_RATIONALE,
  BULK_ACCEPT_THRESHOLD_FALLBACK,
  MAX_SUBMIT_RETRIES_FALLBACK,
  findRecoveredWithDraftLoss,
  readQ9Payload,
  rehydrateChoiceFromAnswer,
  sanitiseInMemoryDraft,
  sanitiseQ9Submission,
  type G4ValidationSettings,
  type Q9Option,
  type Q9Payload,
} from '@/lib/g4Sanitiser'

import {
  G4RuleGroupAccordion,
  getDecisionRule,
  groupDecisionsByRule,
} from './G4RuleGroupAccordion'

// Re-export the shared sanitiser surface so external consumers
// (existing tests, future helpers) can keep importing from this
// module path. The implementations live in ``@/lib/g4Sanitiser``.
export {
  BULK_ACCEPT_RATIONALE,
  BULK_BATCH_RATIONALE,
  findRecoveredWithDraftLoss,
  rehydrateChoiceFromAnswer,
  sanitiseQ9Submission,
  type Q9Option,
  type Q9Payload,
}

// Back-compat re-export for tests that still import
// ``coerceToAcceptOriginal`` from this module. The implementation
// lives in ``@/lib/g4SubmitRecovery`` alongside the retry loop that
// uses it (Bug #25 [v3]).
export { coerceToAcceptOriginal }

export interface ContentAwareG4Props {
  project: Project
  runId: number
  onApproved?: () => void
}

// Phase-E rewrite-status badge (option 1). Tracks whether the
// Rewriter's AFTER actually resolves the flagged INCOSE rule(s) so the
// architect can spot REW-002-style "fixes" that didn't fix anything.
type RewriteStatus = 'auto_fixed' | 'unresolved' | 'needs_review' | null

interface RewriteDetails {
  id: string         // The audit-diff finding id, e.g. "F-001"
  reqId: string      // The requirement the finding targets, e.g. "BR-003"
  severity: string   // "critical" | "suggested"
  rule: string       // INCOSE rule code, e.g. "R4"
  reason: string     // The audit's explanation of the violation
  before: string     // Original requirement statement
  after: string      // Proposed rewrite — only populated after /rewrites/propose
  status: RewriteStatus       // null until /rewrites/propose runs
  statusReason: string        // Short human-readable explanation
}

// REW-001 ↔ F-001. The Critic mints REW-### internally but the
// audit-diff renders F-### headers (Phase E split). The gate
// decisions still carry REW-### ids for backward compat — this
// helper bridges the two id spaces so a single lookup table can
// service both.
function rewToFinding(id: string): string {
  if (/^REW-\d{3,}$/i.test(id)) return 'F-' + id.slice(4)
  return id // already F-### or unrecognised — pass through
}

export function ContentAwareG4Gate({ project, runId, onApproved }: ContentAwareG4Props) {
  const queryClient = useQueryClient()
  const gateNumber = 4
  const draftKey = gateDraftKey(project.id, runId, gateNumber)
  const decisions = useGateDraft(draftKey)
  const setDecisionsDraft = useGateDraftStore((s) => s.setDraft)
  const clearDecisionsDraft = useGateDraftStore((s) => s.clearDraft)
  const setDecisions = (next: GateDecisionRecord[]) =>
    setDecisionsDraft(draftKey, next)
  const [auditDiff, setAuditDiff] = useState('')
  const [rqsJson, setRqsJson] = useState<any>(null)
  // Bumping ``refreshTick`` forces the audit-diff/rqs.json effect to
  // re-run after a "Submit & Reevaluate" cycle so the side-by-side
  // diff and the RQS card mirror the freshly re-audited artifacts.
  const [refreshTick, setRefreshTick] = useState(0)
  // G4 UX clarity (2026-05-19) — when the architect first lands on a
  // gate emitted with the full Critic finding list, "suggested" polish
  // findings are folded under a collapsible. The state flips when the
  // architect explicitly expands them. Default false so a 100-finding
  // gate doesn't render 100 cards on first paint.
  const [showSuggested, setShowSuggested] = useState(false)
  // Sprint 4 (2026-05-29) — rule grouping. Real G4s ship 200+
  // decisions where R4 alone is 30%+ of the list; the architect's
  // workflow is "fix R4 at source, accept all R4 rewrites." Default
  // off for back-compat; toggle persisted in localStorage so the
  // architect's preference sticks across reloads.
  const [groupByRule, setGroupByRule] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem('ai_alm.g4.groupByRule') === '1'
    } catch {
      return false
    }
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        'ai_alm.g4.groupByRule',
        groupByRule ? '1' : '0',
      )
    } catch {
      // ignore storage exceptions (private mode / disabled storage)
    }
  }, [groupByRule])
  const [previousRqs, setPreviousRqs] = useState<{ overall: number; ears: number; incose: number } | null>(null)
  // Map of finding id → materialised rewrite (after text + status badge).
  // The audit-diff alone only carries ``Before:`` lines now (Phase E
  // split — see plan §6.2), so we rely on the Rewriter's per-finding
  // files in ``runs/<n>/phase4/rewrites/<F-###>.md`` for the "After
  // (Proposed)" side of the diff. The status / status_reason come
  // from the /rewrites/propose response (Phase-E option 1) so the UI
  // can render Resolved / Did-not-resolve / Review-carefully badges
  // without having to re-parse each file's frontmatter. Empty until
  // the user hits "Submit & Reevaluate" (which auto-previews first).
  interface MaterializedRewrite {
    after: string
    status: RewriteStatus
    statusReason: string
  }
  const [materializedAfter, setMaterializedAfter] = useState<Map<string, MaterializedRewrite>>(new Map())
  // Phase-E option 1 — when the architect tries to approve a rewrite the
  // classifier flagged as ``unresolved`` or ``needs_review`` we surface a
  // styled confirmation overlay instead of a native ``window.confirm``
  // (the latter blocks the main thread and ignores the dark theme).
  // ``null`` = no prompt active; otherwise we hold the pending decision id
  // and the rewrite metadata used for the on-screen explanation.
  const [confirmFlagged, setConfirmFlagged] = useState<
    | {
        decisionId: string
        status: 'unresolved' | 'needs_review'
        statusReason: string
        proposedAfter: string
      }
    | null
  >(null)

  // PR-C — Rewriter pre-pass partial-completion state. The backend
  // writes ``phase4/rewriter-state.json`` after every per-finding
  // disk write so the panel can render a "47/80 — Re-run Rewriter"
  // banner when a mid-flight failure (server reboot, client
  // disconnect, single-LLM-call exception) left the directory short
  // of files. The "Re-run Rewriter" button re-fires the existing
  // ``/rewrites/propose`` endpoint with just the missing ids — no
  // new backend endpoint needed.
  interface RewriterState {
    expected_finding_ids: string[]
    completed_finding_ids: string[]
    missing_finding_ids: string[]
    expected_count: number
    completed_count: number
    missing_count: number
    complete: boolean
    last_updated: string
  }
  const [rewriterState, setRewriterState] = useState<RewriterState | null>(null)

  const gate = useQuery({
    queryKey: ['gate', project.id, runId, gateNumber],
    queryFn: () => api.getGate(project.id, runId, gateNumber),
    retry: false,
  })

  // Bug #21 [v2] — backend-derived tuning knobs for the W3.1 / W3.2
  // validators. Fetched once per QueryClient lifetime (5-min stale
  // time matches the backend's ``Cache-Control: max-age=300``).
  // ``retry: false`` so a 404 (stale frontend hitting an older
  // backend, or the route mount removed in an emergency rollback)
  // falls through to the hard-coded fallbacks below without burning
  // the retry budget. The render path uses ``data?.x ?? FALLBACK``
  // throughout so first render (before the query resolves) and the
  // 404 path both degrade to today's pre-Bug-#21 behaviour instead
  // of crashing the panel.
  const g4ValidationQuery = useQuery({
    queryKey: ['settings', 'g4_validation'],
    queryFn: () => api.getG4Settings(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
  // Memoised so the Bug #22 ``useEffect`` below has a stable dep —
  // a fresh ``sanitiserOpts`` object identity every render would
  // re-fire the effect unnecessarily (still no infinite loop thanks
  // to the row-level diff in ``sanitiseInMemoryDraft``, but a waste
  // of work).
  const sanitiserOpts: G4ValidationSettings = useMemo(
    () => ({
      rationaleMinChars: g4ValidationQuery.data?.rationale_min_chars,
      bulkAcceptThreshold: g4ValidationQuery.data?.bulk_accept_threshold,
    }),
    [
      g4ValidationQuery.data?.rationale_min_chars,
      g4ValidationQuery.data?.bulk_accept_threshold,
    ],
  )
  const bulkAcceptThreshold =
    g4ValidationQuery.data?.bulk_accept_threshold ?? BULK_ACCEPT_THRESHOLD_FALLBACK
  const maxSubmitRetries =
    g4ValidationQuery.data?.max_submit_retries ?? MAX_SUBMIT_RETRIES_FALLBACK

  useEffect(() => {
    let cancelled = false
    // Two-pronged staleness defence:
    //   (1) Append a per-tick cache-buster to the URL so React Query /
    //       the browser HTTP cache treat each refresh as a new resource.
    //       Without it a page reload after a submit would serve the
    //       prior (cached) ``rqs.json`` body and the panel would render
    //       the old RQS even though disk has the new one.
    //   (2) ``cache: 'no-store'`` belt-and-braces — some browsers /
    //       intermediaries honour the URL key, others honour the
    //       request hint; we want both to miss.
    // We use ``Date.now()`` for the initial load (when ``refreshTick``
    // is still 0) so the very first fetch after a page reload also
    // bypasses any stale cached body from a prior session.
    const bust = refreshTick || Date.now()
    async function load() {
      try {
        const diffRes = await fetch(
          projectFileUrl(project.id, `runs/run-${String(runId).padStart(3, '0')}/phase4/audit-diff.md`)
            + `?t=${bust}`,
          { cache: 'no-store' },
        )
        if (diffRes.ok && !cancelled) setAuditDiff(await diffRes.text())
      } catch { /* ignore */ }

      try {
        const rqsRes = await fetch(
          projectFileUrl(project.id, `runs/run-${String(runId).padStart(3, '0')}/phase4/rqs.json`)
            + `?t=${bust}`,
          { cache: 'no-store' },
        )
        if (rqsRes.ok && !cancelled) setRqsJson(await rqsRes.json())
      } catch { /* ignore */ }

      // PR-C — fetch the Rewriter pre-pass state. 404 is normal
      // (the file only lands once the Rewriter actually ran), so
      // we treat ``not ok`` as "no banner needed" silently.
      try {
        const stateRes = await fetch(
          projectFileUrl(
            project.id,
            `runs/run-${String(runId).padStart(3, '0')}/phase4/rewriter-state.json`,
          ) + `?t=${bust}`,
          { cache: 'no-store' },
        )
        if (stateRes.ok && !cancelled) {
          setRewriterState(await stateRes.json())
        } else if (!cancelled) {
          setRewriterState(null)
        }
      } catch { /* ignore — banner just won't render */ }
    }
    load()
    return () => { cancelled = true }
  }, [project.id, runId, refreshTick])

  // Rewriter-readiness gate (2026-06-13 UAT). The G4 gate is emitted
  // IMMEDIATELY after the Critic — before the 3-10 min background
  // Rewriter pre-pass materialises the per-finding rewrites — so the
  // affected Q9 panels initially render ``accept_rewrite`` disabled.
  // Acting on the gate during that window (e.g. Auto Approve) can only
  // pick ``accept_original``, producing the "no improvement worth
  // mentioning" outcome the architect reported. ``rewriter-state.json``
  // carries a ``complete`` flag the runner flips to true only after the
  // final per-finding file lands. While the pre-pass is in flight
  // (``rewriterState`` present AND ``!complete``) we (a) poll the state
  // file so the page self-heals even if the WS ``gate_ready`` re-emit
  // event is missed, and (b) lock the destructive bulk actions below.
  //
  // Safe-degradation: a 404 / absent state file means EITHER the
  // pre-pass hasn't written its first checkpoint yet OR there is no
  // pre-pass at all (clean gate, legacy v0.6, or every rewrite already
  // on disk). The runner treats the state file as best-effort (it never
  // raises, and the disk file count — not this file — is the source of
  // truth), so we must NOT block on its absence: ``rewritesPending`` is
  // false when ``rewriterState`` is null, matching today's behaviour.
  const rewritesPending = Boolean(
    rewriterState && rewriterState.complete === false,
  )
  useEffect(() => {
    if (!rewritesPending) return
    const handle = window.setInterval(() => {
      // Bumping refreshTick re-runs the loader effect above (state +
      // rqs + audit-diff) AND refetching the gate picks up the
      // backend's re-emit with the now-enabled accept_rewrite options.
      setRefreshTick((t) => t + 1)
      gate.refetch()
    }, 4_000)
    return () => window.clearInterval(handle)
  }, [rewritesPending, gate])


  // Seed decisions from the freshly-fetched gate payload, reconciling
  // any persisted localStorage draft against the server's decision-id
  // set first. See ``reconcileDraftWithGate`` for the stale-draft
  // hygiene that keeps a deleted-and-recreated project from inheriting
  // the prior project's Approve/Edit/Reject picks.
  useEffect(() => {
    if (!gate.data) return
    reconcileDraftWithGate(draftKey, gate.data)
  }, [gate.data, draftKey])

  // Bug #22 [v2] — in-memory sanitisation pass.
  //
  // After ``reconcileDraftWithGate`` merges the server payload back
  // into the rehydrated draft, ``accept_original`` rows that were
  // auto-approved in a prior tab still carry the server's empty
  // ``payload.choice.rationale`` (the persisted slice deliberately
  // strips ``payload`` via ``partialize`` — see
  // ``stores/gateDraftStore.ts``). The submit-time sanitiser re-seeds
  // them on the wire, but the in-memory state is briefly
  // inconsistent: DevTools shows ``rationale=''`` until the architect
  // hits Submit. This effect closes that gap by running the same
  // sanitiser against the in-memory draft on mount + after any draft
  // change.
  //
  // Loop safety: ``sanitiseInMemoryDraft`` returns ``changed: false``
  // whenever every output row's reference matches the input. The
  // ``setDecisions`` write only fires when at least one row genuinely
  // changed; on a no-op pass the effect is silent, so a second render
  // does not re-trigger it. Idempotent by construction — the W3.1
  // ``length >= floor`` short-circuit makes "re-run on already-
  // seeded" a no-op, and the W3.2 batch_rationale write only fires
  // when the field is missing or empty.
  useEffect(() => {
    if (decisions.length === 0) return
    const { sanitised, changed } = sanitiseInMemoryDraft(
      decisions,
      sanitiserOpts,
    )
    if (changed) setDecisions(sanitised)
    // ``setDecisions`` is a freshly-bound wrapper every render; it's
    // safe to omit from deps because it always points at the same
    // zustand ``setDraft`` action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions, sanitiserOpts])

  const emit = useMutation({
    mutationFn: () => api.createGate(project.id, runId, gateNumber),
    onSuccess: () => {
      clearDecisionsDraft(draftKey)
      gate.refetch()
      // Re-emit re-runs the Critic server-side and overwrites
      // ``phase4/rqs.json`` + ``audit-diff.md``. Without bumping
      // refreshTick the rqs/audit fetch in the effect at the top of
      // this file is keyed only on (project, run) — it won't re-run
      // and the panel keeps rendering the prior score even after the
      // backend's response lands.
      setRefreshTick((t) => t + 1)
      toast({ variant: 'success', title: 'G4 re-emitted' })
    },
    onError: (err) => toast({ variant: 'destructive', title: 'Re-emit failed', description: (err as Error).message }),
  })

  // PR-C — Re-run Rewriter for the missing finding ids only.
  // Fires the existing ``/rewrites/propose`` endpoint with the
  // narrowed selection so we never re-do work that already landed
  // on disk. Triggered by the partial-completion banner.
  const rerunRewriter = useMutation({
    mutationFn: () => {
      if (!rewriterState || !rewriterState.missing_finding_ids.length) {
        return Promise.resolve(null)
      }
      return api.proposeRewrites(
        project.id,
        runId,
        rewriterState.missing_finding_ids,
      )
    },
    onSuccess: () => {
      // Re-fetch the state file + the gate so the banner clears
      // and the panel's per-card accept_rewrite previews populate.
      setRefreshTick((t) => t + 1)
      gate.refetch()
      toast({
        variant: 'success',
        title: 'Rewriter re-run complete',
        description: 'Reload of the panel will show the new rewrites.',
      })
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Rewriter re-run failed',
        description: (err as Error).message,
      }),
  })

  // ``submit`` takes the decisions list explicitly so callers can pass
  // either the current state or a freshly auto-approved snapshot (the
  // Approve & Continue button passes the latter when ``pendingCount > 0``
  // — see the click handler near the button). Reading ``decisions`` from
  // closure would race with ``setDecisions`` in the same event tick.
  //
  // Sanitises the payload through ``sanitiseQ9Submission`` first so the
  // Approve & Continue path benefits from the same W3.1 (rationale
  // floor) + W3.2 (batch_rationale) protection as Submit & Reevaluate.
  // Without this hook, clicking Approve & continue on a Q9 gate with
  // empty-rationale ``accept_original`` rows still 422s with the
  // "accept_original on critical severity requires rationale of >= 20
  // chars (W3.1)" diagnostic the architect originally reported.
  const submit = useMutation({
    mutationFn: async (toSubmit: GateDecisionRecord[]) => {
      // Prime W3.1/W3.2 up front (happy path passes on the first call),
      // then submit THROUGH the recovery loop — the same one Submit &
      // Reevaluate uses (Bug #25). Without this, the Approve & Continue
      // path had NO 422 recovery: a stale choice or a rewrite that no
      // longer converges dead-ended in a toast and trapped the architect
      // at the export gate. submitWithRecovery flips the offending rows
      // to "Accept original" (logged to accepted-violations.json) and
      // retries, so this exit can actually complete.
      const primed = sanitiseQ9Submission(toSubmit, sanitiserOpts)
      let lastGate:
        | Awaited<ReturnType<typeof api.submitGateDecisions>>
        | undefined
      const { updated, totalRecovered } = await submitWithRecovery({
        initialAttempt: primed,
        maxRetries: maxSubmitRetries,
        bulkAcceptThreshold,
        submit: async (attempt) => {
          lastGate = await api.submitGateDecisions(
            project.id,
            runId,
            gateNumber,
            attempt,
          )
          return lastGate
        },
      })
      if (totalRecovered > 0) {
        toast({
          variant: 'success',
          title: 'Auto-recovered flagged rewrites',
          description: `${totalRecovered} finding${
            totalRecovered === 1 ? '' : 's'
          } fell back to "Accept original" because the Critic's rewrite no longer converges on this run.`,
        })
      }
      const draftLoss = findRecoveredWithDraftLoss(toSubmit, updated)
      if (draftLoss.length > 0) {
        const count = draftLoss.length
        const idsPreview = draftLoss.slice(0, 3).join(', ')
        const overflow = count > 3 ? ` (+${count - 3} more)` : ''
        toast({
          variant: 'warning',
          title: `Cleared draft text on ${count} ${count === 1 ? 'card' : 'cards'}`,
          description: `${idsPreview}${overflow} — fell back to "Accept original", so typed notes on those cards were dropped.`,
        })
      }
      // Keep the in-memory draft consistent with what was actually sent
      // (seeded rationales + any post-422 accept_original flips).
      setDecisions(updated)
      return lastGate
    },

    onSuccess: (g) => {
      gate.refetch()
      // Same reason as the emit path above: the submit-decisions route
      // re-runs the Critic internally (``_apply_q9_g4_submission`` calls
      // ``run_critic``) and applies rewrites to ``srd.md``, so the on-
      // disk RQS moves the moment this resolves. Force the rqs/audit
      // fetch to refire so the panel matches what the backend wrote.
      setRefreshTick((t) => t + 1)
      if (g?.status === 'approved') {
        toast({ variant: 'success', title: 'G4 approved', description: 'Ready to run Phase 5.' })
      }
    },
    onError: (err) => {
      // 2026-07-03 UX fix — a submit can "fail" on the client while
      // having fully landed on the server (proxy timeout while the
      // pre-fix post-rewrite re-audit ran inline, or any transient
      // network blip). Refetch the gate so the panel reflects the
      // server's actual state; if the decisions DID persist, the
      // approved banner + Continue button appear without forcing
      // the architect to submit twice.
      gate.refetch()
      toast({ variant: 'destructive', title: 'Could not submit decisions', description: (err as Error).message })
    },
  })

  // ---------------------------------------------------------------------- //
  // Auto-Approve — flip every still-pending decision to "approved" without
  // touching ones the architect has already edited or rejected. This is the
  // "trust the Critic" fast-path that lets the user move on to the audit
  // step instead of clicking through each rewrite individually.
  // ---------------------------------------------------------------------- //
  const pendingCount = decisions.filter((d) => d.status === 'pending').length

  /** Pure compute step: returns the auto-approved decision list plus a
   *  count of pending items that needed a manual decision (Q6 status
   *  ``unresolved`` / ``needs_review`` — we leave those untouched so a
   *  bulk-approve never rubber-stamps a rewrite the Rewriter flagged).
   *  Shared between the standalone Auto-Approve button and the Approve
   *  & Continue button's "approve all + submit" fast path so the two
   *  surfaces agree on what "approve everything" means.
   */
  const computeAutoApproved = (): {
    next: GateDecisionRecord[]
    approved: number
    skipped: number
  } => {
    let skipped = 0
    const next = decisions.map((d) => {
      if (d.status !== 'pending') return d
      // Q9 four-option panel — pick ``accept_rewrite`` when the
      // Critic produced a usable rewrite (option enabled), otherwise
      // fall back to ``accept_original``. Accepting the original on
      // an unresolved finding is safe because the backend logs it to
      // accepted-violations.json and the gate still completes.
      const q9 = readQ9Payload(d)
      if (q9) {
        const acceptRewrite = q9.options.find((o) => o.id === 'accept_rewrite')
        const useRewrite = Boolean(acceptRewrite?.enabled && acceptRewrite?.preview_text?.trim())
        const optionId = useRewrite ? 'accept_rewrite' : 'accept_original'
        const status: GateDecisionStatus = 'approved'
        // W3.1 (Hardening) — when we programmatically pick
        // ``accept_original``, the backend validator requires a
        // rationale of ``settings.rationale_min_chars`` (default 20)
        // for critical/high severity findings. The architect didn't
        // type one (this is the bulk-approve fast path), so seed a
        // default that comfortably exceeds the floor and clearly
        // identifies the source in the audit trail. Without this
        // seed Submit & Reevaluate would 422 with N rows of
        // "accept_original on critical severity requires rationale
        // of >= 20 chars (W3.1)" — the exact failure mode the
        // architect reported.
        const existingRationale = (q9.choice?.rationale ?? '').trim()
        const seededRationale =
          optionId === 'accept_original' && existingRationale.length === 0
            ? BULK_ACCEPT_RATIONALE
            : q9.choice?.rationale ?? ''
        const nextChoice: Q9Payload['choice'] = {
          option_id: optionId,
          manual_edit_text: q9.choice?.manual_edit_text ?? '',
          reprompt_note: q9.choice?.reprompt_note ?? '',
          rationale: seededRationale,
        }
        const nextPayload = {
          ...((d.payload as Record<string, unknown>) ?? {}),
          choice: nextChoice,
        }
        return { ...d, status, answer: optionId, payload: nextPayload }
      }
      const details = parsedRewrites.get(d.id)
      if (
        details &&
        (details.status === 'unresolved' || details.status === 'needs_review')
      ) {
        skipped += 1
        return d
      }
      return { ...d, status: 'approved' as GateDecisionStatus }
    })
    return { next, approved: pendingCount - skipped, skipped }
  }

  const autoApproveAll = () => {
    if (pendingCount === 0) {
      toast({ title: 'Nothing to approve', description: 'All decisions already have a status.' })
      return
    }
    const { next, approved, skipped } = computeAutoApproved()
    setDecisions(next)

    if (approved === 0) {
      toast({
        variant: 'destructive',
        title: 'Nothing auto-approvable',
        description: `${skipped} pending ${skipped === 1 ? 'rewrite needs' : 'rewrites need'} a manual decision (Did not resolve / Review carefully).`,
      })
      return
    }
    toast({
      variant: 'success',
      title: `Auto-approved ${approved} ${approved === 1 ? 'decision' : 'decisions'}`,
      description:
        skipped > 0
          ? `Skipped ${skipped} that need a manual decision. Click Submit & Reevaluate to re-run the Critic, or Approve & Continue to finish without another pass.`
          : 'Click Submit & Reevaluate to re-run the Critic, or Approve & Continue to finish without another pass.',
    })
  }

  // ---------------------------------------------------------------------- //
  // Approve & Continue — the "I'm done iterating" escape hatch.
  //
  // Plain submit when every decision is already resolved. When pending
  // decisions remain (typical after Submit & Reevaluate surfaces a fresh
  // batch from the Critic), auto-approve them first so the architect can
  // exit the audit loop in a single click. The Critic loop produces
  // diminishing returns past the second iteration — RQS goes from 0.7578
  // → 0.9043 → 0.9078, with each pass introducing ~80 new "no change"
  // findings. Without this fast path the user is stuck in a never-ending
  // Auto-Approve / Submit & Reevaluate cycle.
  //
  // The standalone Auto-Approve button stays as a separate affordance for
  // architects who want to inspect / edit the auto-approved decisions
  // before submitting. Approve & Continue is "trust me, I'm done."
  // ---------------------------------------------------------------------- //
  const approveAndContinue = () => {
    if (pendingCount === 0) {
      submit.mutate(decisions)
      return
    }
    const { next, approved, skipped } = computeAutoApproved()
    setDecisions(next)
    if (approved === 0) {
      // Every pending row is in the "needs manual decision" bucket
      // (Q6 status unresolved / needs_review). Refuse rather than
      // submit a still-pending payload that the backend will reject.
      toast({
        variant: 'destructive',
        title: 'Cannot auto-approve everything',
        description: `${skipped} pending ${skipped === 1 ? 'rewrite needs' : 'rewrites need'} a manual decision before you can continue.`,
      })
      return
    }
    submit.mutate(next)
  }

  // ---------------------------------------------------------------------- //
  // Helper — pull the materialised "after" text out of a Rewriter file.
  // Each rewrite file (`runs/<n>/phase4/rewrites/<F-###>.md`) is
  // YAML-frontmatter + Markdown body with a "## After" section
  // (see :func:`_render_rewrite_body` in phase4_runner.py). We don't
  // need a Markdown parser — a simple split + line-walk gets the body
  // text reliably and bails on the next "##" heading.
  // ---------------------------------------------------------------------- //
  const fetchMaterializedAfter = async (
    rewritePathsByFinding: Record<string, string>,
    proposals: Array<{
      finding_id: string
      status: RewriteStatus
      status_reason: string
    }>,
  ): Promise<Map<string, MaterializedRewrite>> => {
    // Index the inline proposal status (from /rewrites/propose) so we
    // can attach Resolved / Did-not-resolve / Review-carefully badges
    // to each per-finding entry without re-parsing each file's YAML.
    const statusByFinding = new Map<string, { status: RewriteStatus; reason: string }>()
    for (const p of proposals) {
      statusByFinding.set(p.finding_id, {
        status: p.status,
        reason: p.status_reason,
      })
    }
    const out = new Map<string, MaterializedRewrite>()
    const entries = Object.entries(rewritePathsByFinding)
    await Promise.all(
      entries.map(async ([findingId, relPath]) => {
        try {
          const res = await fetch(projectFileUrl(project.id, relPath))
          if (!res.ok) return
          const text = await res.text()
          // Strip YAML frontmatter then look for "## After" body.
          const afterIdx = text.indexOf('## After')
          if (afterIdx < 0) return
          const afterBody = text.slice(afterIdx + '## After'.length)
          // Stop at next "## " heading (or EOF).
          const stop = afterBody.search(/\n##\s+/)
          const slice = stop < 0 ? afterBody : afterBody.slice(0, stop)
          const cleaned = slice.replace(/^[\s>]+/g, '').trim()
          if (cleaned) {
            const meta = statusByFinding.get(findingId)
            out.set(findingId, {
              after: cleaned,
              status: meta?.status ?? null,
              statusReason: meta?.reason ?? '',
            })
          }
        } catch {
          /* per-file failures are non-fatal — the diff card just shows
             the audit-diff fallback or the placeholder text. */
        }
      }),
    )
    return out
  }

  // ---------------------------------------------------------------------- //
  // Submit & Reevaluate — the audit-loop the user asked for:
  //   1) POST /rewrites/propose  →  Rewriter materialises per-finding
  //                                 ``rewrites/<F-###>.md`` with the
  //                                 corrected ``After:`` text. (Phase E
  //                                 split — see plan §6.3.)
  //   2) POST gate decisions     →  approved/edited rewrites land in
  //                                 brd/srd via apply_post_g4_rewrites
  //                                 (server-side, plan §M4.4.1).
  //   3) POST /phase4            →  Critic re-runs against the now-
  //                                 updated source-of-truth and rewrites
  //                                 audit-diff + rqs.json (server
  //                                 auto-emits a fresh G4).
  // After step 3 we refetch the gate + bump refreshTick so the UI shows
  // any *new* findings, the new EARS / INCOSE / RQS scores, and the
  // updated before/after diffs. The architect can then audit the result
  // before clicking through to Phase 5.
  //
  // We also fetch the materialised "after" text from step 1 so the
  // diff card can immediately show the proposed rewrite preview to the
  // architect (this is what surfaces in the green "After (Proposed)"
  // panel when the Critic deferred rewrite text out of the audit-diff).
  // ---------------------------------------------------------------------- //
  const submitReevaluate = useMutation({
    mutationFn: async () => {
      // Snapshot the current scores so the post-reevaluate UI can show a
      // before/after delta strip without losing the previous numbers.
      if (rqsJson) {
        setPreviousRqs({
          overall: rqsJson.overall,
          ears: rqsJson.breakdown?.ears_conformance ?? 0,
          incose: rqsJson.breakdown?.incose_40 ?? 0,
        })
      }

      // Step 1 — materialise per-finding rewrites for everything the
      // architect approved/edited. We send BOTH id forms so a backend
      // that's strict about either id space resolves cleanly. Rejected
      // decisions are skipped (architect chose not to apply that fix).
      //
      // Q9 short-circuit (plan §9.1): the Q9 four-option panel already
      // ships the "best rewrite" preview in ``payload.options[].preview_text``
      // — the LLM ran during Phase 4 and its output is baked into the
      // gate JSON. The legacy ``parsedRewrites`` map is only consulted
      // by the v0.6 REW-### diff card, NOT by ``Q9FindingPanel`` (which
      // renders directly from the option payload). The server-side
      // ``_apply_q9_g4_submission`` + ``apply_post_g4_rewrites`` reads
      // from the Q9 choice payload too — it does not need the per-finding
      // ``rewrites/<F-###>.md`` files. Calling ``/rewrites/propose`` on
      // a 252-finding Q9 gate would re-run the Rewriter LLM for ~75
      // requirement groups (with Q6 retry loop on each) — easily 10+
      // minutes — and the proxy times out with ERR_EMPTY_RESPONSE long
      // before the call finishes, manifesting as the
      // "Could not preview rewrites — Failed to fetch" error the
      // architect sees mid-Submit. Skip the call when every approved/
      // edited row carries a Q9 payload; the legacy path is preserved
      // for pre-Q9 gates.
      const submitSelections = decisions.filter(
        (d) => d.status === 'approved' || (d.status === 'edited' && (d.answer ?? '').trim().length > 0),
      )
      const legacySelections = submitSelections.filter((d) => !readQ9Payload(d))
      const approvedSelectionIds = legacySelections
        .flatMap((d) => {
          const rewId = d.id.toUpperCase()
          const findingId = rewToFinding(rewId)
          return rewId === findingId ? [rewId] : [rewId, findingId]
        })
      let materializeResult: Awaited<ReturnType<typeof api.proposeRewrites>> | null = null
      if (approvedSelectionIds.length > 0) {
        try {
          materializeResult = await api.proposeRewrites(
            project.id,
            runId,
            approvedSelectionIds,
          )
          // Pull the materialised "After" text out of each per-finding
          // file so the green diff panel shows the proposed rewrite
          // preview before the next Critic pass overwrites the
          // audit-diff.
          if (materializeResult.rewrite_paths) {
            const afterByFinding = await fetchMaterializedAfter(
              materializeResult.rewrite_paths,
              materializeResult.proposals ?? [],
            )
            setMaterializedAfter(afterByFinding)
          }
        } catch (err) {
          // Non-fatal — apply step still works without materialised
          // previews; the architect just won't see the After text in
          // the diff. Surface a warn-level toast and keep going.
          toast({
            variant: 'destructive',
            title: 'Could not preview rewrites',
            description: (err as Error).message,
          })
        }
      }

      // Step 2 — persist decisions + apply approved/edited rewrites to
      // disk. The backend (apply_post_g4_rewrites) reads from the
      // Critic's deterministic seed text, so even when Step 1 is a
      // no-op the apply still works.
      //
      // Q9 recovery — the backend re-derives the panel from a fresh
      // ``run_critic`` pass when the submission lands, so the option
      // ``enabled`` flags can drift from the cached payload (e.g. the
      // Rewriter's best attempt didn't converge on this run, so
      // ``accept_rewrite`` is now disabled). When that happens the
      // resolver returns 422 with ``offending_finding_ids`` —
      // ``submitWithRecovery`` flips those rows to ``accept_original``
      // (a safe terminal pick that logs the finding to
      // accepted-violations.json) and retries up to
      // ``maxSubmitRetries`` times. The loop body and full Bug #25
      // docstring live in ``@/lib/g4SubmitRecovery``.

      // Pre-submit sanitiser — shared with the Approve & Continue
      // path. ``sanitiseQ9Submission`` handles both W3.1 (seed
      // ``BULK_ACCEPT_RATIONALE`` on ``accept_original`` rows whose
      // rationale is empty / sub-floor) and W3.2 (attach
      // ``BULK_BATCH_RATIONALE`` when the bulk-accept count exceeds
      // the threshold). See ``@/lib/g4Sanitiser`` for the W3.1/W3.2
      // contract and an idempotency note. Doing the seed up front
      // means the happy path goes through on the first try.
      const primedDecisions = sanitiseQ9Submission(decisions, sanitiserOpts)

      const { updated: appliedDecisions, totalRecovered } =
        await submitWithRecovery({
          initialAttempt: primedDecisions,
          maxRetries: maxSubmitRetries,
          bulkAcceptThreshold,
          submit: (attempt) =>
            api.submitGateDecisions(project.id, runId, gateNumber, attempt),
        })
      if (totalRecovered > 0) {
        toast({
          variant: 'success',
          title: 'Auto-recovered flagged rewrites',
          description: `${totalRecovered} finding${
            totalRecovered === 1 ? '' : 's'
          } fell back to "Accept original" because the Critic's rewrite no longer converges on this run.`,
        })
      }

      // Bug #19 — surface "lost typed text" when the auto-recovery
      // retry loop wiped ``manual_edit_text`` / ``reprompt_note`` on
      // rows the architect had partially filled in.
      // ``coerceToAcceptOriginal`` unconditionally writes ``''`` to
      // both fields whenever a row falls back to ``accept_original``;
      // that's the right contract for the W3.1 recovery case (the row
      // is converging on "keep original" so manual-edit / reprompt are
      // no longer meaningful) but doing it silently leaves the
      // architect's draft on the floor with no UI signal. The
      // predicate lives in ``@/lib/g4Sanitiser`` so it can be unit-
      // tested without rendering the gate.
      const recoveredWithDraftLoss = findRecoveredWithDraftLoss(
        decisions,
        appliedDecisions,
      )
      if (recoveredWithDraftLoss.length > 0) {
        const count = recoveredWithDraftLoss.length
        const idsPreview = recoveredWithDraftLoss.slice(0, 3).join(', ')
        const overflow = count > 3 ? ` (+${count - 3} more)` : ''
        toast({
          variant: 'warning',
          title: `Cleared draft text on ${count} ${count === 1 ? 'card' : 'cards'}`,
          description: `${idsPreview}${overflow} — the Critic's rewrite no longer converges, so your typed text was dropped when the row fell back to "Accept original". Re-open the affected cards if you want to preserve those notes.`,
        })
      }

      // Persist the sanitised + auto-recovery picks back into the
      // local draft. ``appliedDecisions`` is guaranteed to differ
      // from ``decisions`` by reference because
      // ``sanitiseQ9Submission`` returns a fresh array (every
      // ``.map`` allocates) — and any retry-loop flips inside
      // ``submitWithRecovery`` produce a further-mutated array.
      // Committing unconditionally keeps the in-memory draft
      // consistent with what was actually sent (seeded rationales,
      // optional ``batch_rationale``, post-422 ``accept_original``
      // flips). The previous reference-equality guard read as "only
      // update on change" but was always true; this is the honest
      // version of that behaviour.
      setDecisions(appliedDecisions)

      // Step 3 — re-run Phase 4 (Critic) which:
      //   * re-audits the freshly rewritten brd/srd
      //   * persists a new audit-diff.md / rqs.json
      //   * auto-emits a new G4 gate with any *new* findings
      // ``runPhase4`` returns 202 immediately; await the
      // ``phase_completed`` WS event for the real summary (the
      // backend stamps ``rewrite_count`` / ``rqs_overall`` into the
      // event payload — see ``_phase_4_body`` in phases.py).
      await api.runPhase4(project.id, runId)
      return waitForPhaseCompletion(project.id, runId, 4)
    },

    onSuccess: (event) => {
      gate.refetch()
      setRefreshTick((t) => t + 1)
      queryClient.invalidateQueries({ queryKey: ['artifact-index', project.id, runId] })
      queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      const payload = event?.payload ?? {}
      const newCount = Number(payload.rewrite_count ?? 0)
      const rqsOverall = Number(payload.rqs_overall ?? 0)
      toast({
        variant: 'success',
        title: 'Re-audit complete',
        description:
          newCount === 0
            ? `Critic found no new findings. RQS: ${rqsOverall.toFixed(4)}`
            : `Critic surfaced ${newCount} new ${newCount === 1 ? 'finding' : 'findings'}. RQS: ${rqsOverall.toFixed(4)}`,
      })
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Submit & reevaluate failed',
        description: (err as Error).message,
      }),
  })

  const allDecided = decisions.length > 0 && decisions.every((d) => d.status !== 'pending')
  // ``gateCleared`` flips the action row from the Auto-Approve / Submit
  // & Reevaluate / Approve & Continue triplet to a single "Run Phase 5"
  // CTA. We must NOT flip while a Submit & Reevaluate is in flight,
  // because that mutation:
  //   1. persists decisions → backend rolls the gate up to "modified"
  //      immediately (server says "resolved with edits")
  //   2. THEN kicks off a 5-10 min Phase-4 re-audit
  //   3. ON SUCCESS — re-emits a fresh G4 with new pending decisions
  // Step 1 satisfies ``status === "modified"`` long before step 3
  // completes, so without this guard the UI exposes a premature
  // "Run Phase 5" button while the Critic is still working. Once the
  // mutation resolves, gate.data is already refetched to the new
  // pending shape and the triplet renders again — no manual toggle
  // needed.
  const gateCleared =
    !submitReevaluate.isPending &&
    (gate.data?.status === 'approved' || gate.data?.status === 'modified')

  const updateDecision = (id: string, patch: Partial<GateDecisionRecord>) =>
    setDecisions(decisions.map((d) => (d.id === id ? { ...d, ...patch } : d)))

  const missing = gate.error instanceof ApiError && gate.error.status === 404

  // Parse audit-diff.md into a map keyed by BOTH the F-### finding id
  // and the legacy REW-### rewrite id, so callers can do a single lookup
  // regardless of which id space the surrounding code happens to use.
  //
  // The Phase E audit-diff format (plan §6.2) emits:
  //
  //     ### F-001 — BR-001 (critical, R4)
  //     - Reason: Undefined term: Clinical
  //     - Before: The Clinical Toxicology Application shall …
  //
  // No more "After" line — that lives in the per-finding rewrite files
  // materialised by /rewrites/propose. The legacy v0.6 format with
  // "### REW-### — REQ (sev, rule)" + "After:" is also tolerated so
  // older audit-diff files keep rendering.
  const parsedRewrites = useMemo(() => {
    if (!auditDiff) return new Map<string, RewriteDetails>()

    const lines = auditDiff.split('\n')
    const rewrites = new Map<string, RewriteDetails>()

    let currentId: string | null = null
    let currentReqId = ''
    let currentSeverity = ''
    let currentRule = ''
    let currentReason = ''
    let currentBefore = ''
    let currentAfter = ''

    const flush = () => {
      if (!currentId) return
      // Prefer the materialised after from /rewrites/propose if present,
      // then the audit-diff inline After (legacy format), then a clean
      // empty string so the UI can show "Not yet generated". We resolve
      // through both the original id and its sibling (REW ↔ F) so the
      // first lookup that lands wins regardless of which space the
      // audit-diff happens to use for this row.
      const mat =
        materializedAfter.get(currentId) ??
        materializedAfter.get(rewToFinding(currentId))
      const details: RewriteDetails = {
        id: currentId,
        reqId: currentReqId,
        severity: currentSeverity,
        rule: currentRule,
        reason: currentReason,
        before: currentBefore,
        after: mat?.after ?? currentAfter,
        status: mat?.status ?? null,
        statusReason: mat?.statusReason ?? '',
      }
      rewrites.set(currentId, details)
      // Mirror the entry under the alternate id space so a REW-001 lookup
      // hits the F-001 row and vice-versa.
      const alt =
        currentId.startsWith('F-')
          ? 'REW-' + currentId.slice(2)
          : currentId.startsWith('REW-')
            ? 'F-' + currentId.slice(4)
            : null
      if (alt) rewrites.set(alt, details)
    }

    // Header regex tolerates both id spaces.
    //   ### F-001 — BR-001 (critical, R4)         ← Phase E
    //   ### REW-001 — BR-001 (critical, R4)       ← legacy
    const headerRe = /^###\s+((?:F|REW)-\d+)\s+—\s+([A-Z]+-\d+)\s+\((critical|suggested),\s*(R\d+)\)/

    for (const line of lines) {
      const m = line.match(headerRe)
      if (m) {
        flush()
        currentId = m[1].toUpperCase()
        currentReqId = m[2]
        currentSeverity = m[3]
        currentRule = m[4]
        currentReason = ''
        currentBefore = ''
        currentAfter = ''
        continue
      }
      if (currentId) {
        if (line.startsWith('- Reason: ')) currentReason = line.substring(10).trim()
        else if (line.startsWith('- Before: ')) currentBefore = line.substring(10).trim()
        else if (line.startsWith('- After: ')) currentAfter = line.substring(9).trim()
      }
    }
    flush()
    return rewrites
  }, [auditDiff, materializedAfter])

  // Sprint 4 (2026-05-29) — per-group bulk approve. Mirrors the
  // Auto-Approve discipline (Rewriter-status guardrail: unresolved /
  // needs_review are skipped; accept_original on a Q9 finding gets
  // a seeded rationale that clears the >=20 char floor). Scoped to
  // one rule code so the architect can fix R4 once at the glossary
  // and accept all 66 R4 rewrites in one click. The duplication
  // with `computeAutoApproved` is deliberate — refactoring that
  // function's signature would touch approveAndContinue and several
  // toast call sites; this stays small.
  const approveGroup = (ruleCode: string) => {
    let approved = 0
    let skipped = 0
    const next = decisions.map((d) => {
      if (d.status !== 'pending') return d
      if (getDecisionRule(d) !== ruleCode) return d
      const q9 = readQ9Payload(d)
      if (q9) {
        const acceptRewrite = q9.options.find((o) => o.id === 'accept_rewrite')
        const useRewrite = Boolean(
          acceptRewrite?.enabled && acceptRewrite?.preview_text?.trim(),
        )
        const optionId = useRewrite ? 'accept_rewrite' : 'accept_original'
        const existingRationale = (q9.choice?.rationale ?? '').trim()
        const seededRationale =
          optionId === 'accept_original' && existingRationale.length === 0
            ? BULK_ACCEPT_RATIONALE
            : q9.choice?.rationale ?? ''
        const nextChoice: Q9Payload['choice'] = {
          option_id: optionId,
          manual_edit_text: q9.choice?.manual_edit_text ?? '',
          reprompt_note: q9.choice?.reprompt_note ?? '',
          rationale: seededRationale,
        }
        const nextPayload = {
          ...((d.payload as Record<string, unknown>) ?? {}),
          choice: nextChoice,
        }
        approved += 1
        return {
          ...d,
          status: 'approved' as GateDecisionStatus,
          answer: optionId,
          payload: nextPayload,
        }
      }
      const details = parsedRewrites.get(d.id)
      if (
        details &&
        (details.status === 'unresolved' || details.status === 'needs_review')
      ) {
        skipped += 1
        return d
      }
      approved += 1
      return { ...d, status: 'approved' as GateDecisionStatus }
    })
    setDecisions(next)
    if (approved === 0 && skipped > 0) {
      toast({
        variant: 'destructive',
        title: `No ${ruleCode} findings auto-approvable`,
        description: `${skipped} ${ruleCode} ${skipped === 1 ? 'rewrite needs' : 'rewrites need'} a manual decision (Did not resolve / Review carefully).`,
      })
      return
    }
    toast({
      variant: 'success',
      title: `Approved ${approved} ${ruleCode} finding${approved === 1 ? '' : 's'}`,
      description:
        skipped > 0
          ? `Skipped ${skipped} that need a manual decision.`
          : 'Click Submit & Reevaluate to re-run the Critic, or Approve & Continue to finish without another pass.',
    })
  }

  // G4 UX clarity (2026-05-19) — group decisions by severity for
  // rendering. Critical + high + other (= standard G4-D# questions,
  // prevention findings, anything without a severity) always render.
  // Suggested-severity polish folds behind a collapsible so a
  // 100-decision gate doesn't render 100 cards on first paint.
  const visibleDecisions = useMemo(() => {
    if (showSuggested) return decisions
    return decisions.filter((d) => {
      const payload = d.payload as Record<string, unknown> | null | undefined
      const sev = String(
        (payload && (payload as { severity?: unknown }).severity) || ''
      ).toLowerCase()
      return sev !== 'suggested'
    })
  }, [decisions, showSuggested])

  // G4 UX clarity (2026-05-19) — hidden-polish auto-fill.
  //
  // When the architect hasn't expanded the polish section, those
  // suggested-severity decisions still need a status before Submit
  // can fire (the backend's gate enforcer counts ANY pending row as
  // blocking, regardless of severity). Pre-fix the architect would
  // pick options on the 29 visible cards, click Submit, and see
  // "G4 has unanswered decisions" because the 71 hidden polish rows
  // were still pending.
  //
  // The contract we want: hiding polish == "accept these as-is on
  // submit". This effect auto-fills any pending suggested row with
  // ``accept_original`` (matching the existing Auto-Approve button's
  // semantics at line 305-339). Toggling Show back on lets the
  // architect override individual picks — already-decided polish
  // rows are NOT re-touched.
  useEffect(() => {
    if (showSuggested) return
    const { next, touched } = autoFillHiddenPolish(decisions)
    if (touched) setDecisions(next)
    // We intentionally exclude setDecisions from deps to avoid
    // re-running on every render; setDecisions is stable enough
    // for this hook in practice (it's a thin wrapper around a
    // zustand setter).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions, showSuggested])


  return (
    <div className="alm-fadein">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-warn"><AlertCircle className="h-3 w-3" /> Needs you</span>
            <span className="alm-chip">Gate G4</span>
            {decisions.length > 0 && <span className="alm-chip alm-chip-info">{decisions.length} decisions</span>}
            {gate.data?.status && <span className="alm-chip">{gate.data.status}</span>}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">Review Critic Rewrites</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            The Critic audited the Swarm output against INCOSE rules. Review the proposed rewrites side-by-side.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button type="button" onClick={() => emit.mutate()} disabled={emit.isPending} className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50">
            {emit.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Re-emit
          </button>
        </div>
      </div>

      {missing && (
        <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-4 mb-5 text-sm text-slate-300">
          <p className="font-semibold">No gate yet</p>
          <p className="text-xs text-slate-400 mt-1">Click <strong>Re-emit</strong> above to build the G4 artifact.</p>
        </div>
      )}

      {/* PR-C — Rewriter pre-pass partial-completion banner. Renders
          when ``phase4/rewriter-state.json`` reports any missing
          finding ids (a mid-flight server reboot / cancellation
          dropped some rewrites). Re-run hits the existing
          ``/rewrites/propose`` endpoint with the missing-only
          selection. */}
      {rewriterState && rewriterState.missing_count > 0 && (
        <div
          className="rounded-md border border-amber-700/40 bg-amber-950/20 p-4 mb-5 flex items-start gap-3"
          role="alert"
          data-testid="g4-rewriter-pre-pass-banner"
        >
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-400 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-200">
              Rewriter pre-pass partial: {rewriterState.completed_count}/
              {rewriterState.expected_count}
            </p>
            <p className="text-xs text-amber-200/80 mt-1 leading-snug">
              {rewriterState.missing_count} finding
              {rewriterState.missing_count === 1 ? '' : 's'} need a
              rewrite (typically after a server reboot or mid-flight
              cancellation). The architect can either click{' '}
              <strong>Re-run Rewriter</strong> to retry just the
              missing findings, or use Manual Edit / Re-prompt
              per-card to make progress without another LLM pass.
            </p>
          </div>
          <button
            type="button"
            onClick={() => rerunRewriter.mutate()}
            disabled={rerunRewriter.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-amber-400 disabled:opacity-50 shrink-0"
            data-testid="g4-rerun-rewriter"
          >
            {rerunRewriter.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Re-running…
              </>
            ) : (
              <>
                <RefreshCw className="h-3 w-3" />
                Re-run Rewriter
              </>
            )}
          </button>
        </div>
      )}

      {/* Rewriter-readiness banner (2026-06-13 UAT). While the
          background Rewriter pre-pass is still generating per-finding
          rewrites, the bulk actions below are locked (see
          ``rewritesPending``). Surface a clear "still generating —
          don't act yet" signal with live progress so the architect
          knows WHY the buttons are disabled and that the page will
          unlock itself. The poll effect refreshes ``rewriterState``
          every 4 s and refetches the gate, so the count ticks up and
          the banner disappears the moment the pre-pass completes. */}
      {rewritesPending && (
        <div
          className="rounded-md border border-indigo-700/40 bg-indigo-950/20 p-4 mb-5 flex items-start gap-3"
          role="status"
          aria-live="polite"
          data-testid="g4-rewrites-generating-banner"
        >
          <Loader2 className="h-5 w-5 shrink-0 text-indigo-300 mt-0.5 animate-spin" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-indigo-100">
              Rewriter is generating proposed rewrites…{' '}
              {rewriterState
                ? `${rewriterState.completed_count}/${rewriterState.expected_count}`
                : ''}
            </p>
            <p className="text-xs text-indigo-200/80 mt-1 leading-snug">
              The Critic finished and the gate is open so you can read
              the findings, but the per-finding rewrites are still being
              prepared (this can take a few minutes). <strong>Auto
              Approve</strong> and <strong>Submit</strong> are disabled
              until they finish — otherwise they'd fall back to "keep
              original" and your run wouldn't actually improve. This
              banner clears itself automatically when the rewrites are
              ready; no action needed.
            </p>
          </div>
        </div>
      )}

      {gate.isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading gate…
        </div>
      )}

      {/* RQS Summary */}

      {rqsJson && (
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/60 p-4 mb-6">
           <div className="flex items-center justify-between mb-2">
             <div className="text-[10.5px] uppercase tracking-wider text-slate-500">Requirement Quality Score (RQS)</div>
             {previousRqs && (
               <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                 <Sparkles className="h-3 w-3 text-indigo-400" />
                 Reevaluated · prev {previousRqs.overall.toFixed(4)}
               </div>
             )}
           </div>
           <div className="flex items-end gap-3">
             <div className="text-3xl font-bold text-indigo-400">{rqsJson.overall.toFixed(4)}</div>
             <div className="text-sm text-slate-400 mb-1">overall score</div>
             {previousRqs && (
               <ScoreDelta current={rqsJson.overall} previous={previousRqs.overall} />
             )}
           </div>
           <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-300">
             <div>
               <div className="text-slate-500 mb-0.5">EARS</div>
               <div className="font-mono text-indigo-200 flex items-center gap-1.5">
                 {rqsJson.breakdown.ears_conformance.toFixed(2)}
                 {previousRqs && (
                   <ScoreDelta size="xs" current={rqsJson.breakdown.ears_conformance} previous={previousRqs.ears} />
                 )}
               </div>
             </div>
             <div>
               <div className="text-slate-500 mb-0.5">INCOSE</div>
               <div className="font-mono text-indigo-200 flex items-center gap-1.5">
                 {rqsJson.breakdown.incose_40.toFixed(2)}
                 {previousRqs && (
                   <ScoreDelta size="xs" current={rqsJson.breakdown.incose_40} previous={previousRqs.incose} />
                 )}
               </div>
             </div>
             <div>
               <div className="text-slate-500 mb-0.5">Traceability</div>
               <div className="font-mono text-indigo-200">{rqsJson.breakdown.traceability.toFixed(2)}</div>
             </div>
             <div>
               <div className="text-slate-500 mb-0.5">Provenance</div>
               <div className="font-mono text-indigo-200">{rqsJson.breakdown.provenance.toFixed(2)}</div>
             </div>
           </div>
        </div>
      )}

      {/* Decision summary (G4 UX clarity, 2026-05-19). Renders the
          critical / high / suggested / other breakdown plus the
          "X of Y requirements failing INCOSE" math so the architect
          sees up front why a high-decision-count G4 can still ship a
          0.95 overall RQS. The total here always matches the
          ``decisions.length`` chip in the page header. */}
      {decisions.length > 0 && rqsJson && (
        <G4DecisionSummaryCard
          decisions={decisions}
          totalRequirements={
            Array.isArray(rqsJson.per_requirement)
              ? rqsJson.per_requirement.length
              : 0
          }
          showSuggested={showSuggested}
          onToggleSuggested={() => setShowSuggested((v) => !v)}
        />
      )}

      {/* Sprint 4 (2026-05-29) — Rule-grouping toggle. Sits above
          the decision list so the architect's eye finds it next to
          the existing "Show suggested" polish toggle. Off by default
          for back-compat; persists in localStorage so the choice
          sticks across reloads. */}
      {decisions.length > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-slate-400 mb-2">
          <label className="flex items-center gap-1.5 cursor-pointer hover:text-slate-200">
            <input
              type="checkbox"
              checked={groupByRule}
              onChange={(e) => setGroupByRule(e.target.checked)}
              className="rounded border-slate-600 bg-slate-900"
            />
            Group by rule
            {groupByRule && (
              <span className="text-slate-500 ml-1">
                (bulk-approve per group)
              </span>
            )}
          </label>
        </div>
      )}

      {/* Decisions as Diff Viewer */}
      {(() => {
        const renderDecisionCard = (decision: GateDecisionRecord): ReactNode => {
          // ----------------------------------------------------------- //
          // Q9 four-option panel (plan §9.1) — the post-Q9 G4 backend
          // emits findings with id ``F-###`` and a structured payload
          // carrying the rewrite context (before_text, the four
          // options each with their own preview_text + summary, the
          // requirement_id, rule, and severity). Render the
          // requirement-quality side-by-side diff + the four cards
          // exactly the way G3.5 renders its option cards so the
          // architect doesn't lose the rewrite context they need to
          // pick the right option.
          //
          // Without this branch the v0.6 binary Approve/Edit/Reject
          // affordance kicks in and the architect can't see the
          // before/after, the rule citation, or the per-option
          // preview text — which is the bug the user reported as
          // "we lost all the context the proposed rewrites".
          // ----------------------------------------------------------- //
          const q9Payload = readQ9Payload(decision)
          if (q9Payload) {
            return (
              <Q9FindingPanel
                key={decision.id}
                decision={decision}
                payload={q9Payload}
                onChooseOption={(optionId, extras) =>
                  applyQ9Choice(
                    decisions,
                    decision,
                    q9Payload,
                    optionId,
                    extras,
                    setDecisions,
                  )
                }
              />
            )
          }

          // General fallback if no rewrite details
          if (!decision.id.startsWith('REW-')) {
            return (
              <div key={decision.id} className="alm-card">
                 <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 text-sm text-slate-200">{decision.question}</div>
                    <div className="flex gap-1.5 shrink-0 flex-wrap">
                      {(['approved', 'edited', 'rejected'] as GateDecisionStatus[]).map((status) => (
                        <button key={status} type="button" onClick={() => updateDecision(decision.id, { status })} className="alm-decision" data-selected={decision.status === status ? status : undefined}>
                          {status === 'approved' ? <Check className="h-3 w-3" /> : status === 'edited' ? <Edit3 className="h-3 w-3" /> : <X className="h-3 w-3" />} <span>{decisionLabel(status)}</span>
                        </button>
                      ))}
                    </div>
                 </div>
              </div>
            )
          }

          const details = parsedRewrites.get(decision.id)

          return (
            <div key={decision.id} className="alm-card overflow-hidden">
               {/* Header row */}
               <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-slate-700/50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[10.5px] text-indigo-400">{decision.id}</span>
                    <span className="text-[10.5px] font-semibold text-slate-400">→ {details?.reqId}</span>
                    {details && <span className={`alm-chip ${details.severity === 'critical' ? 'alm-chip-warn' : 'alm-chip-info'}`}>{details.severity}</span>}
                    {details && <span className="alm-chip">{details.rule}</span>}
                    {/* Phase-E option 1 — rewrite-status badge. Hidden
                        until the Rewriter has classified this proposal
                        (status === null = pre-propose). */}
                    <RewriteStatusBadge status={details?.status ?? null} />
                  </div>
                  <div className="text-xs text-slate-300">{details?.reason || decision.question}</div>
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap">
                  {(['approved', 'edited', 'rejected'] as GateDecisionStatus[]).map((status) => {
                    // Phase-E option 1 — confirm before approving a
                    // rewrite the classifier flagged as ``unresolved``
                    // or ``needs_review``. Edit / Reject pass through
                    // without a prompt because they're already explicit
                    // architect overrides.
                    const guardedClick = () => {
                      const willApprove = status === 'approved'
                      const flagged = details && (details.status === 'unresolved' || details.status === 'needs_review')
                      if (willApprove && flagged && details) {
                        // Defer the actual mutation until the architect
                        // confirms via the styled overlay below. The
                        // overlay reads from `confirmFlagged` and calls
                        // `updateDecision` itself on confirm.
                        setConfirmFlagged({
                          decisionId: decision.id,
                          status: details.status as 'unresolved' | 'needs_review',
                          statusReason: details.statusReason,
                          proposedAfter: details.after,
                        })
                        return
                      }
                      updateDecision(decision.id, {
                        status,
                        answer:
                          status === 'edited' && !decision.answer && details
                            ? details.after
                            : decision.answer,
                      })
                    }
                    return (
                      <button
                        key={status}
                        type="button"
                        onClick={guardedClick}
                        className="alm-decision"
                        data-selected={decision.status === status ? status : undefined}
                      >
                        {status === 'approved' ? <Check className="h-3 w-3" /> : status === 'edited' ? <Edit3 className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>{decisionLabel(status)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Side-by-side diff. The Phase E audit-diff intentionally
                  ships only ``Before`` text — the proposed ``After`` is
                  materialised by the Rewriter agent on demand (see
                  Submit & Reevaluate above). Render a clear empty-state
                  hint instead of a blank green panel when we don't yet
                  have a materialised after for this finding. */}
              {details && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className="bg-red-950/20 border border-red-900/30 rounded p-2 text-xs text-red-200 whitespace-pre-wrap">
                    <div className="text-[10px] uppercase font-bold text-red-500/50 mb-1">Before</div>
                    {details.before}
                  </div>
                  <div className="bg-emerald-950/20 border border-emerald-900/30 rounded p-2 text-xs text-emerald-200 whitespace-pre-wrap relative">
                     <div className="absolute -left-2.5 top-1/2 -translate-y-1/2 hidden md:block text-slate-600 bg-slate-900 rounded-full"><ArrowRight className="h-4 w-4" /></div>
                    <div className="text-[10px] uppercase font-bold text-emerald-500/50 mb-1">After (Proposed)</div>
                    {details.after ? (
                      details.after
                    ) : (
                      <span className="italic text-emerald-400/60">
                        Rewrite preview not yet materialised — click <strong>Submit & Reevaluate</strong> to generate one for every approved finding.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Phase-E option 1 — explain why this rewrite is flagged
                  so the architect knows whether to Edit, Reject, or
                  fix the prerequisite (glossary entry, charter target)
                  and re-emit. Only renders when the Rewriter classified
                  the proposal as non-``auto_fixed``. */}
              {details && (details.status === 'unresolved' || details.status === 'needs_review') && (
                <RewriteStatusWarning
                  status={details.status}
                  reason={details.statusReason}
                />
              )}

              {/* Edit / Reject textareas */}
              {decision.status === 'edited' && (
                <div className="mt-3">
                   <div className="text-[10px] uppercase font-bold text-amber-500/50 mb-1">Your Edit</div>
                  <textarea
                    value={decision.answer ?? ''}
                    onChange={(e) => updateDecision(decision.id, { answer: e.target.value })}
                    rows={2}
                    placeholder="Edit the proposed rewrite..."
                    className="w-full bg-slate-950/40 border border-amber-700/50 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                  />
                </div>
              )}
              {decision.status === 'rejected' && (
                <div className="mt-3">
                  <textarea
                    value={decision.rejection_reason ?? ''}
                    onChange={(e) => updateDecision(decision.id, { rejection_reason: e.target.value })}
                    rows={2}
                    placeholder="Why are you rejecting this rewrite?"
                    className="w-full bg-slate-950/40 border border-red-700/50 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-red-500/50"
                  />
                </div>
              )}
            </div>
          )
        }
        return groupByRule ? (
          <G4RuleGroupAccordion
            groups={groupDecisionsByRule(visibleDecisions)}
            renderDecision={renderDecisionCard}
            onApproveGroup={approveGroup}
          />
        ) : (
          <div className="space-y-4 mb-6">
            {visibleDecisions.map(renderDecisionCard)}
          </div>
        )
      })()}

      {gate.data?.status === 'approved' && !submitReevaluate.isPending && (
        // Same race guard as ``gateCleared`` below: a Submit & Reevaluate
        // mutation persists the decisions BEFORE Phase 4 finishes
        // re-auditing, so ``gate.data.status`` briefly reads
        // ``"approved"`` (or ``"modified"``) while the Critic is still
        // working. Without ``!submitReevaluate.isPending`` we'd flash
        // "G4 approved — run next phase" green alongside a spinning
        // "Reevaluating…" button — a literal contradiction on screen.
        <div className="rounded-lg p-4 mb-5 border border-emerald-700/40 bg-emerald-950/20 flex items-center gap-2 text-emerald-300 text-sm">
          <CheckCircle2 className="h-4 w-4" /> G4 approved — continue to the next step
        </div>
      )}

      {/* Confirmation overlay — replaces window.confirm() so the
          flagged-rewrite approval prompt matches the dark theme and
          doesn't block the main thread. */}
      {confirmFlagged && (
        <FlaggedApproveDialog
          status={confirmFlagged.status}
          statusReason={confirmFlagged.statusReason}
          onCancel={() => setConfirmFlagged(null)}
          onConfirm={() => {
            const target = decisions.find((d) => d.id === confirmFlagged.decisionId)
            if (target) {
              updateDecision(confirmFlagged.decisionId, {
                status: 'approved',
                answer: target.answer,
              })
            }
            setConfirmFlagged(null)
          }}
        />
      )}

      {/* Primary actions. A gate advances to the NEXT STEP; it never runs
          a phase. Phase 5 runs from the Pipeline Runner once its turn
          comes. */}
      <div className="flex flex-wrap items-center gap-2">
        {gateCleared ? (
          <button type="button" onClick={() => onApproved?.()} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-400 hover:to-indigo-500">
            <CheckCircle2 className="h-4 w-4" /> Continue
          </button>
        ) : (
          <>
            {/* Auto-Approve all pending decisions in one click. Disabled
                when nothing is pending (button surface still readable so
                the user knows the action exists for future re-emits). */}
            <button
              type="button"
              onClick={autoApproveAll}
              disabled={pendingCount === 0 || submitReevaluate.isPending || submit.isPending || rewritesPending}
              className="inline-flex items-center gap-2 rounded-lg border border-indigo-700/60 bg-indigo-950/30 px-4 py-2 text-sm font-semibold text-indigo-200 hover:bg-indigo-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                rewritesPending
                  ? 'The Rewriter is still generating proposed rewrites. Auto Approve is disabled until they finish so it can apply the rewrites instead of falling back to "keep original".'
                  : 'Mark all still-pending decisions as approved (preserves existing edits/rejections)'
              }
            >

              <CheckCheck className="h-4 w-4" />
              Auto Approve
              {pendingCount > 0 && (
                <span className="ml-1 rounded-full bg-indigo-500/30 px-1.5 py-0.5 text-[10px] font-mono text-indigo-100">
                  {pendingCount}
                </span>
              )}
            </button>

            {/* Submit & Reevaluate — applies all approved/edited rewrites
                and re-runs the Critic. Disabled until every decision has a
                non-pending status so the architect can't accidentally
                kick off the loop with stale state. */}
            <button
              type="button"
              data-testid="gate-4-submit"
              onClick={() => submitReevaluate.mutate()}
              disabled={!allDecided || submitReevaluate.isPending || submit.isPending || rewritesPending}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
              title={
                rewritesPending
                  ? 'The Rewriter is still generating proposed rewrites. Submit is disabled until they finish so the rewrites are applied, not skipped.'
                  : !allDecided
                  ? `Resolve the ${pendingCount} pending decision${pendingCount === 1 ? '' : 's'} above before submitting (use Auto Approve to accept all).`
                  : 'Apply all approved/edited rewrites and re-run the Critic to surface any new findings. Click Approve & Continue instead if you’re done iterating.'
              }
            >

              {submitReevaluate.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Reevaluating…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Submit & Reevaluate
                </>
              )}
            </button>

            {/* Approve & Continue — the "I'm done iterating" escape hatch.
                When some decisions are still pending, ``approveAndContinue``
                auto-approves them first so the architect can exit the
                Critic re-audit loop in one click. The standalone Auto
                Approve button is kept for users who want to review the
                auto-approved decisions before submitting. */}
            <button
              type="button"
              onClick={approveAndContinue}
              disabled={
                decisions.length === 0 ||
                submit.isPending ||
                submitReevaluate.isPending
              }
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                pendingCount > 0
                  ? `Auto-approve the ${pendingCount} remaining pending decision${pendingCount === 1 ? '' : 's'} and submit — exits the Critic re-audit loop without another iteration.`
                  : 'Apply rewrites and approve the gate without re-running the Critic. Click Submit & Reevaluate if you want another Critic pass first.'
              }
            >
              {submit.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Approve & continue
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Rewrite status badge (Phase-E option 1). Three colours map to the three
// classifier outcomes; ``null`` (pre-propose) renders nothing so the v0.6
// pre-rewrite UI keeps its old look.
// --------------------------------------------------------------------------- //
function RewriteStatusBadge({ status }: { status: 'auto_fixed' | 'unresolved' | 'needs_review' | null }) {
  if (status === null) return null
  if (status === 'auto_fixed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
        <CheckCircle2 className="h-3 w-3" /> Resolved
      </span>
    )
  }
  if (status === 'unresolved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 text-red-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
        <X className="h-3 w-3" /> Did not resolve
      </span>
    )
  }
  // needs_review
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
      <AlertCircle className="h-3 w-3" /> Review carefully
    </span>
  )
}

// --------------------------------------------------------------------------- //
// Inline warning panel — surfaces the classifier's reason directly under
// the side-by-side diff so the architect knows what's wrong without
// having to mouse-over the badge.
// --------------------------------------------------------------------------- //
function RewriteStatusWarning({
  status,
  reason,
}: {
  status: 'unresolved' | 'needs_review'
  reason: string
}) {
  const isUnresolved = status === 'unresolved'
  const palette = isUnresolved
    ? 'border-red-700/40 bg-red-950/30 text-red-200'
    : 'border-amber-700/40 bg-amber-950/30 text-amber-200'
  const headline = isUnresolved
    ? 'This rewrite did not resolve the flagged rule.'
    : 'This rewrite resolves the flagged rule but needs review.'
  const followup = isUnresolved
    ? 'Use Edited to fix manually, or address the prerequisite (glossary entry, charter target) and Re-emit.'
    : 'Verify the rewrite preserves the original intent before approving.'
  return (
    <div className={`mt-2 rounded-md border ${palette} px-3 py-2 text-[11px] leading-relaxed`}>
      <div className="flex items-start gap-2">
        <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">{headline}</div>
          {reason && <div className="opacity-90 mt-0.5">{reason}</div>}
          <div className="opacity-70 mt-1">{followup}</div>
        </div>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Confirmation overlay shown before the architect approves a rewrite the
// classifier flagged as ``unresolved`` or ``needs_review``. Replaces the
// previous ``window.confirm`` so the prompt matches the dark theme and the
// rest of the gate's affordances. Click-outside / Escape both cancel.
// --------------------------------------------------------------------------- //
function FlaggedApproveDialog({
  status,
  statusReason,
  onCancel,
  onConfirm,
}: {
  status: 'unresolved' | 'needs_review'
  statusReason: string
  onCancel: () => void
  onConfirm: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])

  const isUnresolved = status === 'unresolved'
  const headline = isUnresolved
    ? 'This rewrite did not resolve the flagged rule.'
    : 'This rewrite may need a closer look before approval.'
  const palette = isUnresolved
    ? 'border-red-700/50 bg-red-950/30 text-red-200'
    : 'border-amber-700/50 bg-amber-950/30 text-amber-200'
  const ctaPalette = isUnresolved
    ? 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 shadow-red-500/20'
    : 'bg-gradient-to-b from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-amber-500/20'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm alm-fadein"
      onClick={onCancel}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        className="alm-card max-w-md w-[92%] mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <AlertCircle className={`h-5 w-5 shrink-0 mt-0.5 ${isUnresolved ? 'text-red-400' : 'text-amber-400'}`} />
          <div className="min-w-0">
            <div className="text-base font-semibold text-slate-100">Approve flagged rewrite?</div>
            <div className="text-sm text-slate-300 mt-1">{headline}</div>
            {statusReason && (
              <div className={`mt-3 rounded-md border ${palette} px-3 py-2 text-xs leading-relaxed`}>
                {statusReason}
              </div>
            )}
            <div className="text-[11px] text-slate-400 mt-3">
              Approving will mark this finding ready for the next Critic pass even though the
              classifier flagged it. You can still <strong>Edit</strong> or <strong>Reject</strong> instead.
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-white shadow-lg ${ctaPalette}`}
          >
            <Check className="h-3.5 w-3.5" /> Approve anyway
          </button>
        </div>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Tiny inline component — renders a "+0.04" / "−0.12" pill so the post-
// reevaluate UI can show the score delta against the previous run without
// pulling in a chart library. Kept here (not in `ui/`) because it's only
// meaningful inside the G4 reevaluate workflow.
// --------------------------------------------------------------------------- //
// --------------------------------------------------------------------------- #
// G4 decision summary card (G4 UX clarity, 2026-05-19) — surfaces the
// critical / high / suggested / other breakdown plus the
// "X of Y requirements failing INCOSE" math up-front so a high-count
// gate doesn't surprise the architect. Reads ``rqs.json::per_requirement``
// for the denominator; degrades gracefully when that file is missing.
// --------------------------------------------------------------------------- #
function G4DecisionSummaryCard({
  decisions,
  totalRequirements,
  showSuggested,
  onToggleSuggested,
}: {
  decisions: GateDecisionRecord[]
  totalRequirements: number
  showSuggested: boolean
  onToggleSuggested: () => void
}) {
  const summary = useMemo(() => summariseG4Decisions(decisions), [decisions])

  // Pull the readable severity stats together for the header row.
  const severityCells: Array<{
    key: string
    label: string
    count: number
    chipClass: string
    title: string
  }> = [
    {
      key: 'critical',
      label: 'Critical',
      count: summary.critical,
      chipClass: 'border-red-700/40 bg-red-950/30 text-red-300',
      title:
        'Critical findings drag INCOSE-40. Their host requirement fails ' +
        'the axis until the finding is resolved (or accepted with ' +
        'justification).',
    },
    {
      key: 'high',
      label: 'High',
      count: summary.high,
      chipClass: 'border-amber-700/40 bg-amber-950/30 text-amber-300',
      title:
        'High-priority findings (typically architect-decision audits). ' +
        'No direct RQS axis impact, but they catch a real correctness gap.',
    },
    {
      key: 'suggested',
      label: 'Suggested',
      count: summary.suggested,
      chipClass: 'border-slate-700/40 bg-slate-900/40 text-slate-400',
      title:
        'Suggested-severity polish findings. They do NOT move any RQS ' +
        'axis — clearing them improves readability and audit trail, ' +
        'not the score.',
    },
    {
      key: 'other',
      label: 'Other',
      count: summary.other,
      chipClass: 'border-slate-700/40 bg-slate-900/40 text-slate-400',
      title:
        'Other gate questions (standard G4-D# items, prevention ' +
        'linter findings, anything without a severity). No direct axis ' +
        'impact.',
    },
  ]

  const incoseRowsClean = Math.max(0, totalRequirements - summary.requirementsFailingIncose)
  const incoseRatio =
    totalRequirements > 0 ? incoseRowsClean / totalRequirements : null

  return (
    <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 mb-4" data-testid="g4-summary-card">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">
          Decision summary
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {summary.total} total {summary.total === 1 ? 'decision' : 'decisions'}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {severityCells
          .filter((c) => c.count > 0)
          .map((c) => (
            <span
              key={c.key}
              className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium ${c.chipClass}`}
              title={c.title}
            >
              <span className="font-mono">{c.count}</span>
              <span>{c.label}</span>
            </span>
          ))}
        {summary.suggested > 0 && (
          <button
            type="button"
            onClick={onToggleSuggested}
            className="ml-auto inline-flex items-center gap-1 rounded border border-slate-700/40 px-2 py-0.5 text-[11px] text-slate-300 hover:bg-white/5"
            data-testid="g4-toggle-suggested"
            title={
              showSuggested
                ? 'Hide the polish findings — they will be auto-accepted (accept_original) on Submit so the architect can move on without clicking through each one.'
                : 'Expand the polish findings so each one renders as its own card. Currently they are auto-accepted as accept_original on Submit.'
            }
          >
            {showSuggested ? 'Hide' : 'Review'} {summary.suggested} polish{' '}
            {summary.suggested === 1 ? 'finding' : 'findings'}
            {!showSuggested && (
              <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-500">
                (auto-accept)
              </span>
            )}
          </button>
        )}
      </div>
      {/* INCOSE math callout — explains the per-requirement gate that
          makes INCOSE-40 stay in the high-0.8s range even when the
          decision count looks big. */}
      {totalRequirements > 0 && (
        <div className="rounded border border-slate-700/40 bg-slate-950/40 px-3 py-2 text-[11.5px] text-slate-300">
          {summary.requirementsFailingIncose === 0 ? (
            <>
              <span className="text-emerald-300 font-medium">
                All {totalRequirements} requirements
              </span>{' '}
              are clear of critical violations — INCOSE-40 axis is at its
              ceiling. The remaining {summary.total} findings are
              advisory.
            </>
          ) : (
            <>
              <span className="text-red-300 font-medium">
                {summary.requirementsFailingIncose}
              </span>{' '}
              of{' '}
              <span className="font-medium text-slate-200">
                {totalRequirements}
              </span>{' '}
              requirements have at least one{' '}
              <span className="font-medium">critical</span> violation —
              fix those to clear the INCOSE-40 axis (currently{' '}
              <span className="font-mono text-indigo-200">
                {incoseRatio !== null ? incoseRatio.toFixed(4) : '—'}
              </span>
              ). The other {summary.suggested + summary.other + summary.high}{' '}
              {summary.suggested + summary.other + summary.high === 1
                ? 'finding is'
                : 'findings are'}{' '}
              advisory — they don't move any RQS axis.
            </>
          )}
        </div>
      )}
    </div>
  )
}


function ScoreDelta({
  current,
  previous,
  size = 'sm',
}: {
  current: number
  previous: number
  size?: 'xs' | 'sm'
}) {
  const delta = current - previous
  // Treat ≤0.0049 as "no meaningful change" so we don't flash a pill for
  // floating-point noise when the rewrites didn't actually move scores.
  if (Math.abs(delta) < 0.005) {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-slate-700/40 text-slate-400 font-mono ${
          size === 'xs' ? 'text-[9px] px-1.5 py-0' : 'text-[10px] px-2 py-0.5'
        }`}
      >
        no change
      </span>
    )
  }
  const positive = delta > 0
  return (
    <span
      className={`inline-flex items-center rounded-full font-mono ${
        positive
          ? 'bg-emerald-500/20 text-emerald-300'
          : 'bg-red-500/20 text-red-300'
      } ${size === 'xs' ? 'text-[9px] px-1.5 py-0' : 'text-[10px] px-2 py-0.5'}`}
    >
      {positive ? '+' : '−'}
      {Math.abs(delta).toFixed(2)}
    </span>
  )
}

// --------------------------------------------------------------------------- //
// Q9 four-option G4 panel (plan §9.1) — types + helpers live in
// ``@/lib/g4Sanitiser`` (BULK_*_RATIONALE constants, Q9Option /
// Q9Payload types, readQ9Payload, sanitiseQ9Submission). The backend's
// ``_apply_q9_g4_submission`` (api/routes/gates.py) reads
// ``payload.choice`` to dispatch — so this panel's job is to (a)
// render the four cards with full context, (b) write the architect's
// pick into ``payload.choice`` and bump the decision's ``status`` so
// the gate becomes submittable.
// --------------------------------------------------------------------------- //

/**
 * Apply the architect's choice to the in-flight decision draft. The
 * backend ``_apply_q9_g4_submission`` resolver reads
 * ``payload.choice.option_id`` (and the matching free-text fields when
 * present) — we mirror that contract here, plus map the Q9 option to a
 * legacy ``GateDecisionStatus`` so the on-disk gate row also persists
 * a non-pending status (the backend re-derives the per-row status
 * after resolving anyway, but we want the local "all decided" check
 * to flip the Submit buttons on as soon as the architect picks).
 */
function applyQ9Choice(
  allDecisions: GateDecisionRecord[],
  decision: GateDecisionRecord,
  payload: Q9Payload,
  optionId: string,
  extras: { manualEditText?: string; repromptNote?: string },
  setDecisions: (next: GateDecisionRecord[]) => void,
) {
  // Map the Q9 option to a v0.6-compatible status so the local
  // ``allDecided`` check + the gate JSON's row-level status stay
  // truthful even before the backend resolver runs.
  let status: GateDecisionStatus
  switch (optionId) {
    case 'accept_original':
    case 'accept_rewrite':
      status = 'approved'
      break
    case 'manual_edit':
      status = 'edited'
      break
    case 'reprompt':
      // ``reprompt`` is intentionally mapped to ``rejected`` so the
      // architect sees a clear "this finding still needs work" state
      // until the next Critic pass. The Q9 resolver handles the actual
      // reprompt queue server-side; the local status is for UX only.
      status = 'rejected'
      break
    default:
      status = 'pending'
  }

  // Surface the option_id as the answer for the legacy submit path
  // (``submit & continue`` falls back to v0.6 where ``answer`` is the
  // edited text; for Q9 the resolver re-derives final text from the
  // choice payload, but writing answer = option_id keeps the row
  // descriptive in the on-disk gate JSON).
  let answer: string | null
  switch (optionId) {
    case 'accept_original':
    case 'accept_rewrite':
    case 'reprompt':
      answer = optionId
      break
    case 'manual_edit':
      answer = (extras.manualEditText ?? '').trim() || null
      break
    default:
      answer = null
  }

  const nextChoice: Q9Payload['choice'] = {
    option_id: optionId,
    manual_edit_text: extras.manualEditText ?? '',
    reprompt_note: extras.repromptNote ?? '',
    rationale: payload.choice?.rationale ?? '',
  }
  const nextPayload = {
    ...((decision.payload as Record<string, unknown>) ?? {}),
    choice: nextChoice,
  }

  setDecisions(
    allDecisions.map((d) =>
      d.id === decision.id
        ? {
            ...d,
            status,
            answer,
            payload: nextPayload,
          }
        : d,
    ),
  )
}

// --------------------------------------------------------------------------- //
// Q9FindingPanel — renders the four-option card layout for a single
// ``F-###`` finding. Used in place of the legacy REW-### binary
// affordance so the architect keeps the rewrite context (Before vs.
// After preview, rule citation, per-option summary + preview text) in
// front of them while picking. Mirrors the option-card visual language
// of ``ContentAwareG35Gate`` so the surfaces feel consistent across
// gates.
// --------------------------------------------------------------------------- //
function Q9FindingPanel({
  decision,
  payload,
  onChooseOption,
}: {
  decision: GateDecisionRecord
  payload: Q9Payload
  onChooseOption: (
    optionId: string,
    extras: { manualEditText?: string; repromptNote?: string },
  ) => void
}) {
  const choice = payload.choice ?? null
  const selectedOptionId = choice?.option_id ?? null

  // Local buffers for free-text inputs. Keeping these out of the
  // decisions store means the architect can flip back and forth
  // between manual_edit / reprompt without losing typed text. We
  // seed from the persisted choice payload so a re-emit / refocus
  // round-trip doesn't wipe the architect's draft text either.
  //
  // The "Accept best rewrite" preview also seeds the manual editor
  // when the architect first lands on it — usually the architect
  // wants to start from the rewrite and tweak, not type from
  // scratch.
  const acceptRewriteOption = payload.options.find((o) => o.id === 'accept_rewrite')
  const initialManualSeed =
    choice?.manual_edit_text?.trim() ||
    acceptRewriteOption?.preview_text?.trim() ||
    payload.before_text
  const [manualEditText, setManualEditText] = useState<string>(initialManualSeed)
  const [repromptNote, setRepromptNote] = useState<string>(
    choice?.reprompt_note ?? '',
  )

  // Pull the four canonical options out of the payload. The backend
  // guarantees the order (accept_original, accept_rewrite,
  // manual_edit, reprompt) but we still look them up by id so a
  // future reordering doesn't silently break the layout.
  const acceptOriginal = payload.options.find((o) => o.id === 'accept_original')
  const manualEdit = payload.options.find((o) => o.id === 'manual_edit')
  const reprompt = payload.options.find((o) => o.id === 'reprompt')

  const beforeText = payload.before_text || acceptOriginal?.preview_text || ''
  const afterText = acceptRewriteOption?.preview_text ?? ''
  const afterEnabled = Boolean(acceptRewriteOption?.enabled)

  // Plan F1.3 — render INCOSE vs completeness vs consistency
  // findings distinguishably. Same Q9 four-option panel, but the
  // architect needs to know at a glance whether they're being asked
  // to fix a grammar issue (INCOSE), a missing implementation detail
  // (completeness C1..C7), or a cross-phase mismatch (consistency).
  const ruleInfo = classifyG4Rule(payload.rule)

  return (
    <div className="alm-card overflow-hidden" data-testid="gate-4-row">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-slate-700/50">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono text-[10.5px] text-indigo-400">{decision.id}</span>
            {payload.requirement_id && (
              <span className="text-[10.5px] font-semibold text-slate-400">
                → {payload.requirement_id}
              </span>
            )}
            {payload.severity && (
              <span
                className={`alm-chip ${payload.severity === 'critical' ? 'alm-chip-warn' : 'alm-chip-info'}`}
              >
                {payload.severity}
              </span>
            )}
            {ruleInfo.kind !== 'incose' && (
              <span
                className={`alm-chip ${ruleInfo.kindChipClass}`}
                title={ruleInfo.kindTitle}
                data-testid={`gate-4-rule-kind-${ruleInfo.kind}`}
              >
                {ruleInfo.kindLabel}
              </span>
            )}
            {payload.rule && (
              <span className="alm-chip" title={ruleInfo.tooltip}>
                {ruleInfo.shortLabel}
              </span>
            )}
            {(() => {
              // Axis-impact chip — explains whether THIS specific
              // finding moves an RQS axis. Critical-INCOSE/Completeness
              // findings drag INCOSE; everything else is advisory.
              const impact = axisImpactForFinding(payload.rule, payload.severity)
              return (
                <span
                  className={`alm-chip ${
                    impact.movesScore
                      ? 'alm-chip-warn'
                      : 'border-slate-700/40 bg-slate-900/40 text-slate-400'
                  }`}
                  title={impact.tooltip}
                  data-testid={`gate-4-axis-impact-${impact.axis.replace(/[^a-z0-9]/gi, '-')}`}
                >
                  {impact.chipLabel}
                </span>
              )
            })()}
            {selectedOptionId && (
              <span className="text-[9.5px] uppercase tracking-wide text-emerald-400/80">
                · {q9OptionShortLabel(selectedOptionId)}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-300">{decision.question}</div>
        </div>
      </div>

      {/* Side-by-side diff — Before vs Best Rewrite preview. We keep
          this above the option cards so the architect always has the
          requirement statement + the Critic's best fix in view while
          they decide. */}
      {(beforeText || afterText) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div className="bg-red-950/20 border border-red-900/30 rounded p-2 text-xs text-red-200 whitespace-pre-wrap">
            <div className="text-[10px] uppercase font-bold text-red-500/50 mb-1">
              Before
            </div>
            {beforeText || (
              <span className="italic text-red-400/60">
                Original requirement statement not available.
              </span>
            )}
          </div>
          <div className="bg-emerald-950/20 border border-emerald-900/30 rounded p-2 text-xs text-emerald-200 whitespace-pre-wrap relative">
            <div className="absolute -left-2.5 top-1/2 -translate-y-1/2 hidden md:block text-slate-600 bg-slate-900 rounded-full">
              <ArrowRight className="h-4 w-4" />
            </div>
            <div className="text-[10px] uppercase font-bold text-emerald-500/50 mb-1">
              After (Best rewrite)
            </div>
            {afterEnabled && afterText ? (
              afterText
            ) : (
              <span className="italic text-emerald-400/60">
                No rewrite attempt converged. Use <strong>Manual edit</strong> or{' '}
                <strong>Re-prompt</strong> below to seed the next pass.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Four-option cards. We render them in the canonical order
          (accept_original, accept_rewrite, manual_edit, reprompt) so
          the keyboard / muscle-memory order is stable across panels.
          Use a 2-column grid so each card has enough horizontal room
          for the per-option summary + preview text without wrapping
          word-by-word into a thin column. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        {payload.options.map((opt) => (

          <Q9OptionCard
            key={opt.id}
            opt={opt}
            selected={selectedOptionId === opt.id}
            onClick={() => {
              if (!opt.enabled) return
              onChooseOption(opt.id, {
                manualEditText:
                  opt.id === 'manual_edit'
                    ? manualEditText
                    : choice?.manual_edit_text ?? '',
                repromptNote:
                  opt.id === 'reprompt'
                    ? repromptNote
                    : choice?.reprompt_note ?? '',
              })
            }}
          />
        ))}
      </div>

      {/* Inline editor for manual_edit. We always seed with the best
          rewrite so the architect can tweak rather than type from
          scratch, but they can clear it any time. */}
      {selectedOptionId === 'manual_edit' && manualEdit && (
        <div className="mt-3">
          <div className="text-[10px] uppercase font-bold text-amber-500/60 mb-1">
            Your replacement text
          </div>
          <textarea
            data-testid="gate-4-manual-edit-text"
            value={manualEditText}
            onChange={(e) => {
              setManualEditText(e.target.value)
              onChooseOption('manual_edit', {
                manualEditText: e.target.value,
                repromptNote,
              })
            }}
            rows={2}
            placeholder="Write the corrected requirement statement…"
            className="w-full bg-slate-950/40 border border-amber-700/50 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
          />
          {!manualEditText.trim() && (
            <div className="mt-1 text-[10.5px] text-amber-400/70 italic">
              The gate won't accept an empty manual edit — paste the corrected statement above.
            </div>
          )}
        </div>
      )}

      {/* Inline note input for reprompt — the Rewriter feeds this back
          into the LLM as architect-supplied guidance on the next pass. */}
      {selectedOptionId === 'reprompt' && reprompt && (
        <div className="mt-3">
          <div className="text-[10px] uppercase font-bold text-indigo-400/70 mb-1 flex items-center gap-1">
            <MessageSquarePlus className="h-3 w-3" /> Re-prompt note
          </div>
          <textarea
            data-testid="gate-4-reprompt-note"
            value={repromptNote}
            onChange={(e) => {
              setRepromptNote(e.target.value)
              onChooseOption('reprompt', {
                manualEditText,
                repromptNote: e.target.value,
              })
            }}
            rows={2}
            placeholder="e.g. ``Use a quantified target — milliseconds — and drop the absolute term``"
            className="w-full bg-slate-950/40 border border-indigo-700/50 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
          />
          {!repromptNote.trim() && (
            <div className="mt-1 text-[10.5px] text-indigo-300/70 italic">
              The gate won't accept an empty re-prompt — describe what the next attempt should change.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Q9OptionCard({
  opt,
  selected,
  onClick,
}: {
  opt: Q9Option
  selected: boolean
  onClick: () => void
}) {
  const isReprompt = opt.id === 'reprompt'
  const isManualEdit = opt.id === 'manual_edit'
  const Icon =
    opt.id === 'accept_original'
      ? Check
      : opt.id === 'accept_rewrite'
        ? Sparkles
        : isManualEdit
          ? Edit3
          : MessageSquarePlus
  return (
    <button
      type="button"
      data-testid={`gate-4-option-${opt.id}`}
      onClick={onClick}
      disabled={!opt.enabled}
      className={[
        'text-left rounded-md border px-3 py-2.5 transition disabled:opacity-50 disabled:cursor-not-allowed',
        selected
          ? 'border-indigo-500/70 bg-indigo-950/30 ring-1 ring-indigo-500/50'
          : 'border-slate-700/60 bg-slate-900/40 hover:border-slate-600 hover:bg-slate-900/60',
      ].join(' ')}
      title={!opt.enabled ? 'This option is disabled for this finding.' : undefined}
    >
      <div className="flex items-start gap-2">
        <Icon
          className={[
            'h-3.5 w-3.5 shrink-0 mt-0.5',
            selected ? 'text-indigo-300' : 'text-slate-500',
          ].join(' ')}
        />
        <div className="min-w-0 flex-1">
          <div
            className={[
              'text-[12px] font-semibold leading-tight',
              selected ? 'text-indigo-200' : 'text-slate-200',
            ].join(' ')}
          >
            {opt.label}
          </div>
          <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">
            {opt.summary}
          </div>
          {/* Preview text — shows the architect what the row will look
              like if they pick this option. Suppressed for ``reprompt``
              (preview is empty by contract) and ``manual_edit`` (the
              preview is just the original; the architect's edit lives
              in the textarea below). */}
          {!isReprompt && !isManualEdit && opt.preview_text && (
            <div className="mt-2 rounded bg-slate-950/50 border border-slate-800/80 px-2 py-1.5 text-[11px] text-slate-300 whitespace-pre-wrap font-mono leading-snug">
              {opt.preview_text}
            </div>
          )}
          {!opt.enabled && (
            <div className="mt-1.5 text-[10px] uppercase tracking-wide text-slate-500">
              disabled
            </div>
          )}
        </div>
      </div>
    </button>
  )
}

function q9OptionShortLabel(optionId: string): string {
  switch (optionId) {
    case 'accept_original':
      return 'kept original'
    case 'accept_rewrite':
      return 'accepted rewrite'
    case 'manual_edit':
      return 'manual edit'
    case 'reprompt':
      return 're-prompt queued'
    default:
      return optionId
  }
}


/**
 * Plan F1.3 — classify a G4 rule id into its finding kind and produce
 * friendlier labels for the panel chip. Three kinds:
 *   * ``incose``       — R1..R38 grammar checks (existing behaviour).
 *   * ``completeness`` — C1..C7 implementation-detail gaps (F1.1).
 *   * ``consistency``  — constraint↔requirement mismatches (F1.2).
 *
 * The unknown bucket renders the raw rule id so a new rule kind
 * shows up identifiably in the panel even before this lookup
 * learns about it.
 */
// --------------------------------------------------------------------------- #
// Axis-impact helper (G4 UX clarity, 2026-05-19) — for every (rule, severity)
// pair, returns which RQS axis the finding actually moves and whether it
// drags the score. Mirrors the per-row gate at
// ``services/critic_service.py::_compute_breakdown``:
//
//     incose_pass = sum(
//         1 for r in results
//         if all(v.severity != "critical" for v in r.violations)
//     )
//
// i.e. INCOSE-40 is a PER-REQUIREMENT pass/fail metric — a row with any
// ``critical`` violation fails the axis. ``suggested`` rows are advisory
// polish and don't move any axis. This helper exposes that gate to the
// architect inline so the count of decisions is no longer surprising
// (e.g. "100 decisions but INCOSE 0.85" → "25 of these are critical and
// land on 7 requirements; the rest are polish").
// --------------------------------------------------------------------------- #
export type G4AxisImpact = {
  axis: 'INCOSE-40' | 'EARS' | 'Traceability' | 'Provenance' | 'none'
  /** True when this finding's severity drags an axis score. */
  movesScore: boolean
  /** Short label rendered as a chip on each row. */
  chipLabel: string
  /** Tooltip explaining why this rule/severity does or doesn't move the score. */
  tooltip: string
}

export function axisImpactForFinding(
  rule: string | undefined | null,
  severity: string | undefined | null,
): G4AxisImpact {
  const sev = (severity || '').toLowerCase()
  const ruleInfo = classifyG4Rule(rule)

  // Critical INCOSE / Completeness violations fail their host row's
  // INCOSE-40 axis check (the backend's ``incose_pass`` predicate
  // gates on ``severity != "critical"`` across ALL violations on a
  // requirement, not just INCOSE-tagged ones).
  if (sev === 'critical' && (ruleInfo.kind === 'incose' || ruleInfo.kind === 'completeness')) {
    return {
      axis: 'INCOSE-40',
      movesScore: true,
      chipLabel: 'Drags INCOSE',
      tooltip:
        'Critical violation — its host requirement fails the INCOSE-40 axis. ' +
        'Fix the requirement (or accept-with-justification) to clear the axis.',
    }
  }
  // 'high' severity (architect-decision audit, F-AD-DU-*) — high priority
  // but not an axis-mover. Shown so the architect prioritises but doesn't
  // assume the RQS will improve by resolving it.
  if (sev === 'high') {
    return {
      axis: 'none',
      movesScore: false,
      chipLabel: 'High · no axis',
      tooltip:
        'High-priority finding (typically an architect-decision audit) but ' +
        'no direct RQS axis impact. Resolve for correctness, not for score.',
    }
  }
  // Suggested INCOSE / Completeness — polish, no axis move.
  if (sev === 'suggested' && (ruleInfo.kind === 'incose' || ruleInfo.kind === 'completeness')) {
    return {
      axis: 'none',
      movesScore: false,
      chipLabel: 'Polish · no axis',
      tooltip:
        'Suggested-severity polish finding. The RQS axes are unaffected — ' +
        'addressing it improves readability and audit trail, not the score.',
    }
  }
  // Consistency rules — cross-phase mismatches, advisory.
  if (ruleInfo.kind === 'consistency') {
    return {
      axis: 'none',
      movesScore: false,
      chipLabel: 'Consistency',
      tooltip:
        'Cross-phase consistency advisory — does not move an RQS axis but ' +
        'flags a real mismatch (a constraint disagrees with a requirement).',
    }
  }
  // Default fallback — covers unknown rule kinds and prevention findings.
  return {
    axis: 'none',
    movesScore: false,
    chipLabel: 'No axis',
    tooltip:
      'Advisory finding — no direct RQS axis impact. Resolve for ' +
      'correctness / audit-trail quality.',
  }
}


// --------------------------------------------------------------------------- #
// Decision summariser (G4 UX clarity, 2026-05-19) — buckets the gate
// decision list into "drags score", "high priority no-axis", "polish",
// and "other", and computes the unique-requirements count for the
// "X of Y requirements failing INCOSE" message.
// --------------------------------------------------------------------------- #
export type G4Summary = {
  total: number
  critical: number          // axis-moving findings
  high: number              // high severity but no axis impact (AD, etc.)
  suggested: number         // polish — no axis impact
  other: number             // standard G4 questions, prevention, anything else
  requirementsFailingIncose: number  // unique requirement_ids of critical findings
}

/**
 * Auto-fill any pending ``suggested``-severity decisions with the
 * ``accept_original`` Q9 choice (or a plain ``status=approved`` for
 * legacy non-Q9 rows). Used by the G4 panel when polish findings are
 * collapsed under the "Review N polish findings" toggle — hiding the
 * polish implies "accept these as-is on Submit", and this helper
 * makes that contract real so the gate enforcer doesn't block on
 * pending suggested rows the architect never opened.
 *
 * The helper is pure: it never mutates the input and only returns a
 * new array when at least one row was changed. ``touched`` reports
 * whether anything actually shifted so the caller can skip a
 * setDecisions round-trip in the no-op case.
 *
 * Non-suggested decisions, decisions whose status is already non-
 * pending, and the rare consistency-rule decisions all pass through
 * unchanged.
 */
export function autoFillHiddenPolish(decisions: GateDecisionRecord[]): {
  next: GateDecisionRecord[]
  touched: number
} {
  let touched = 0
  const next = decisions.map((d) => {
    if (d.status !== 'pending') return d
    const payload = d.payload as Record<string, unknown> | null | undefined
    const sev = String(
      (payload && (payload as { severity?: unknown }).severity) || ''
    ).toLowerCase()
    if (sev !== 'suggested') return d
    touched += 1
    const q9 = readQ9Payload(d)
    if (q9) {
      // Mirror computeAutoApproved's Q9 branch but always pick
      // ``accept_original`` for polish — the architect didn't even
      // look at the rewrite preview, so accepting the original
      // requirement text is the conservative default.
      //
      // W3.1 (Hardening) seed: when a suggested-severity row is
      // actually escalated to critical/high upstream (e.g. the
      // Critic reclassifies on re-emit), an empty rationale would
      // 422 on Submit. The backend's W3.1 floor only fires on
      // critical/high so suggested rows are safe today — but
      // populating the rationale defensively costs nothing and
      // keeps the audit trail consistent with computeAutoApproved.
      const existingRationale = (q9.choice?.rationale ?? '').trim()
      const nextChoice: Q9Payload['choice'] = {
        option_id: 'accept_original',
        manual_edit_text: q9.choice?.manual_edit_text ?? '',
        reprompt_note: q9.choice?.reprompt_note ?? '',
        rationale:
          existingRationale.length === 0
            ? BULK_ACCEPT_RATIONALE
            : q9.choice?.rationale ?? '',
      }
      return {
        ...d,
        status: 'approved' as GateDecisionStatus,
        answer: 'accept_original',
        payload: {
          ...((d.payload as Record<string, unknown>) ?? {}),
          choice: nextChoice,
        },
      }
    }
    return { ...d, status: 'approved' as GateDecisionStatus }
  })
  return { next, touched }
}


export function summariseG4Decisions(decisions: GateDecisionRecord[]): G4Summary {
  let critical = 0
  let high = 0
  let suggested = 0
  let other = 0
  const failingReqs = new Set<string>()

  for (const d of decisions) {
    const payload = d.payload as Record<string, unknown> | null | undefined
    const sev = String((payload && (payload as { severity?: unknown }).severity) || '').toLowerCase()
    const reqId = String((payload && (payload as { requirement_id?: unknown }).requirement_id) || '')

    if (sev === 'critical') {
      critical += 1
      if (reqId) failingReqs.add(reqId)
    } else if (sev === 'high') {
      high += 1
    } else if (sev === 'suggested') {
      suggested += 1
    } else {
      other += 1
    }
  }
  return {
    total: decisions.length,
    critical,
    high,
    suggested,
    other,
    requirementsFailingIncose: failingReqs.size,
  }
}


export function classifyG4Rule(rule: string | undefined | null): {
  kind: 'incose' | 'completeness' | 'consistency' | 'unknown'
  kindLabel: string
  kindChipClass: string
  kindTitle: string
  shortLabel: string
  tooltip: string
} {
  const id = (rule || '').trim()
  if (!id) {
    return {
      kind: 'unknown',
      kindLabel: 'unknown',
      kindChipClass: '',
      kindTitle: '',
      shortLabel: '—',
      tooltip: 'No rule id on this finding.',
    }
  }
  // Completeness — C1..C7.
  if (/^C[1-7]$/.test(id)) {
    const detail = COMPLETENESS_LABELS[id] ?? 'completeness gap'
    return {
      kind: 'completeness',
      kindLabel: 'Completeness',
      kindChipClass: 'alm-chip-info',
      kindTitle: 'Implementation-detail gap — the requirement is grammatically clean but a developer cannot build it without more spec.',
      shortLabel: id,
      tooltip: `${id}: ${detail}`,
    }
  }
  // Consistency — kind strings rather than ids.
  if (id === 'low_confidence_underpins_shall' || id === 'assumed_marker_underpins_shall' || id === 'unresolved_requirement_link') {
    return {
      kind: 'consistency',
      kindLabel: 'Consistency',
      kindChipClass: 'alm-chip-warn',
      kindTitle: 'Cross-phase mismatch — a constraint and a requirement disagree about confidence or anchor.',
      shortLabel: CONSISTENCY_LABELS[id] ?? id,
      tooltip: id.replace(/_/g, ' '),
    }
  }
  // INCOSE — R1..R38.
  if (/^R\d{1,2}$/.test(id)) {
    return {
      kind: 'incose',
      kindLabel: 'INCOSE',
      kindChipClass: 'alm-chip',
      kindTitle: 'INCOSE GtWR-v4 grammar finding.',
      shortLabel: id,
      tooltip: `INCOSE rule ${id}`,
    }
  }
  return {
    kind: 'unknown',
    kindLabel: 'finding',
    kindChipClass: '',
    kindTitle: '',
    shortLabel: id,
    tooltip: id,
  }
}


const COMPLETENESS_LABELS: Record<string, string> = {
  C1: 'Math reference required',
  C2: 'Notification channel required',
  C3: 'Schema / data-dictionary anchor required',
  C4: 'External-integration failure mode required',
  C5: 'Configurable default + override mechanism required',
  C6: 'Secret-storage mechanism required',
  C7: 'Auth lifecycle (refresh / revoke) required',
}


const CONSISTENCY_LABELS: Record<string, string> = {
  low_confidence_underpins_shall: 'low-confidence constraint',
  assumed_marker_underpins_shall: 'assumed constraint',
  unresolved_requirement_link: 'stale link',
}

// ``extractOffendingFindingIds`` and ``coerceToAcceptOriginal`` live
// in ``@/lib/g4SubmitRecovery`` alongside the ``submitWithRecovery``
// loop that uses them (Bug #25 [v3]). ``coerceToAcceptOriginal`` is
// re-exported above for back-compat with existing tests.



